import React, { useState, useEffect, useRef, useMemo, useCallback, Suspense, lazy } from 'react';
import { 
  getDbState, 
  addBooking, 
  addExpense, 
  addInsight, 
  deleteBooking, 
  deleteExpense, 
  deleteInsight,
  preloadDemo,
  clearAllData,
  saveDb,
  addStream,
  removeStream,
  updateBookingStatus
} from './dbService';
import { Booking, Expense, Insight, ServiceType, ExpenseCategory, LinkedType } from './types';
import { Dashboard, BookingsList, ExpensesList, JLensList, InvoicesList } from './components/Views';
import { AddBookingModal, AddExpenseModal, AddInsightModal } from './components/Modals';
import { LandingView } from './components/LandingView';
import { AIConciergeWidget } from './components/AIConciergeWidget';
import { getBusinessSettings, saveBusinessSettings } from './utils/businessSettings';
import { useVoiceFeatureFlag } from './utils/featureFlags';
import { BackupReminderToast } from './components/BackupReminderToast';
import { recordBackupAction, triggerQuickJSONBackup } from './utils/backupManager';
import { ScrollNavigator } from './components/ScrollNavigator';
import { PWAInstallPrompt } from './components/PWAInstallPrompt';
import { PWAUpdateBanner } from './components/PWAUpdateBanner';
import { UserAccountBadge } from './components/UserAccountBadge';
import { SEO } from './components/SEO';
import { getSeoConfigForPath } from './utils/seoMetadata';
import { auth, firebaseSignOut, onAuthStateChanged, User } from './lib/firebase';
import { subscribeToCloudVault, syncStateToCloud, fetchCloudVault, initBackgroundSyncListeners, getLastSyncTimestamp, setLastSyncTimestamp } from './utils/cloudSyncService';
import { PaywallGuard } from './components/PaywallGuard';
import { checkSubscriptionLimits, getSubscriptionData } from './utils/subscriptionManager';
import { activateUserSubscription, deactivateUserSubscription } from './utils/userManagement';
import { TrialCountdownBanner } from './components/TrialCountdownBanner';
import { TrialLifecyclePrompts } from './components/TrialLifecyclePrompts';
import { LanguageSelector } from './components/LanguageSelector';
import { useI18n } from './utils/i18n';
import { PrivacyAmount } from './components/PrivacyAmount';
import { ExportCenter } from './components/ImportExportCenter';
import { WelcomeAboardView } from './components/WelcomeAboardView';
import { OnboardingView } from './components/OnboardingView';
import { BusinessHQView } from './components/BusinessHQView';
import { SubscriptionBillingView } from './components/SubscriptionBillingView';
import { AuthModal } from './components/AuthModal';
import { QuickClarityDashboard } from './components/QuickClarityDashboard';
import { QuickStartWizard } from './components/QuickStartWizard';

// Resilient Dynamic Import with automatic exponential retry to prevent "Failed to fetch dynamically imported module" in iframe environments
function lazyWithRetry<T extends React.ComponentType<any>>(
  componentImport: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    let attempts = 0;
    const maxAttempts = 3;
    while (attempts < maxAttempts) {
      try {
        return await componentImport();
      } catch (error) {
        attempts++;
        if (attempts >= maxAttempts) {
          console.error(`Dynamic import failed after ${maxAttempts} attempts:`, error);
          throw error;
        }
        console.warn(`Retrying dynamic import (attempt ${attempts + 1}/${maxAttempts}) due to network/cache transient:`, error);
        await new Promise(r => setTimeout(r, attempts * 250));
      }
    }
    return await componentImport();
  });
}

// High-impact Code Splitting: Lazy-load heavy secondary views and modals
const AICofounderAssistantView = lazyWithRetry(() => import('./components/AICofounderAssistantView').then(m => ({ default: m.AICofounderAssistantView })));
const CalendarManager = lazyWithRetry(() => import('./components/CalendarManager').then(m => ({ default: m.CalendarManager })));
const FocusRoadmapView = lazyWithRetry(() => import('./components/FocusRoadmapView').then(m => ({ default: m.FocusRoadmapView })));
const CohortPilotPortalView = lazyWithRetry(() => import('./components/CohortPilotPortalView').then(m => ({ default: m.CohortPilotPortalView })));
const PublicBookingView = lazyWithRetry(() => import('./components/PublicBookingView').then(m => ({ default: m.PublicBookingView })));
const WhatIsAzuraOSView = lazyWithRetry(() => import('./components/WhatIsAzuraOSView').then(m => ({ default: m.WhatIsAzuraOSView })));
const CSVImportModal = lazyWithRetry(() => import('./components/ImportExportCenter').then(m => ({ default: m.CSVImportModal })));
const AIReconciliationHub = lazyWithRetry(() => import('./components/AIReconciliationHub').then(m => ({ default: m.AIReconciliationHub })));

// Lazy-load Modals (only loaded when triggered)
const TermsModal = lazyWithRetry(() => import('./components/TermsModal').then(m => ({ default: m.TermsModal })));
const PrivacyModal = lazyWithRetry(() => import('./components/PrivacyModal').then(m => ({ default: m.PrivacyModal })));
const SecurityComplianceModal = lazyWithRetry(() => import('./components/SecurityComplianceModal').then(m => ({ default: m.SecurityComplianceModal })));
const FAQModal = lazyWithRetry(() => import('./components/FAQModal').then(m => ({ default: m.FAQModal })));
const ContactModal = lazyWithRetry(() => import('./components/ContactModal').then(m => ({ default: m.ContactModal })));
const RefundPolicyModal = lazyWithRetry(() => import('./components/RefundPolicyModal').then(m => ({ default: m.RefundPolicyModal })));
const FeedbackBugModal = lazyWithRetry(() => import('./components/FeedbackBugModal').then(m => ({ default: m.FeedbackBugModal })));
const WhatIsAzuraOSModal = lazyWithRetry(() => import('./components/WhatIsAzuraOSModal').then(m => ({ default: m.WhatIsAzuraOSModal })));
const VoiceTranscriptionModal = lazyWithRetry(() => import('./components/VoiceTranscriptionModal').then(m => ({ default: m.VoiceTranscriptionModal })));
const InviteFounderModal = lazyWithRetry(() => import('./components/InviteFounderModal').then(m => ({ default: m.InviteFounderModal })));
const HardPaywallLockoutModal = lazyWithRetry(() => import('./components/HardPaywallLockoutModal').then(m => ({ default: m.HardPaywallLockoutModal })));
const CohortOnboardingModal = lazyWithRetry(() => import('./components/CohortOnboardingModal').then(m => ({ default: m.CohortOnboardingModal })));

import { 
  Sparkles, 
  Plus, 
  Calendar, 
  DollarSign, 
  Brain, 
  Info,
  Sliders,
  AlertTriangle,
  CheckCircle,
  TrendingDown,
  TrendingUp,
  ArrowRight,
  X,
  ArrowUp,
  ChevronDown,
  ExternalLink,
  Layers,
  Globe,
  Target,
  Building2,
  Eye,
  EyeOff,
  UserPlus,
  ShieldCheck,
  ShieldAlert,
  HelpCircle,
  FileText,
  Lock,
  Mail,
  RotateCcw,
  CloudOff,
  Compass,
  CreditCard,
  Bug,
  MoreVertical,
  Menu,
  Clock,
  LogOut,
  LogIn,
  Settings,
  Download,
  Upload,
  Zap,
  Linkedin,
  Twitter,
  MessageCircle,
  Video,
  Presentation,
  Award,
  Mic,
  Car,
  Scale
} from 'lucide-react';
import { calculateMargin, getMarginStatus } from './utils/margin';

import { 
  getApprovedUsers, 
  isUserApproved, 
  addApprovedUser, 
  removeApprovedUser,
  initAllowedUsersSync,
  getUserTrialStatus,
  isUserSuperAdmin
} from './utils/userManagement';

export { 
  getApprovedUsers, 
  isUserApproved, 
  addApprovedUser, 
  removeApprovedUser 
};

export interface MarginImpact {
  type: 'booking' | 'expense' | 'delete_booking' | 'delete_expense';
  itemName: string;
  amount: number;
  oldMargin: number;
  newMargin: number;
  threshold: number;
  crossedBelow: boolean;
  crossedAbove: boolean;
  timestamp: number;
}

export default function App() {
  // DB State
  const [state, setState] = useState(getDbState());
  const { t, lang } = useI18n();
  
  // Helper to parse normalized path from window location or hash
  const getNormalizedPath = () => {
    const rawHash = window.location.hash.replace(/^#/, '');
    const rawPath = (rawHash && rawHash !== '/') ? rawHash : window.location.pathname;
    const cleanPath = (rawPath.split('?')[0].split('#')[0] || '/').toLowerCase();

    // Map common aliases to canonical lowercase paths
    if (cleanPath === '/whatisazuraos' || cleanPath === '/what-is-azuraos' || cleanPath === '/whatisazura' || cleanPath === '/about' || cleanPath === '/explainer') {
      return '/what-is-azuraos';
    }
    if (cleanPath === '/booking' || cleanPath === '/book') {
      return '/book';
    }
    if (cleanPath === '/dashboard' || cleanPath === '/app') {
      return '/dashboard';
    }
    if (cleanPath === '/admin' || cleanPath === '/super-admin' || cleanPath === '/admindashboard' || cleanPath === '/admin-dashboard' || cleanPath === '/dealership' || cleanPath === '/dealership-pilot' || cleanPath === '/dealership-proposal' || cleanPath === '/motor-pilot') {
      return '/admin';
    }
    if (cleanPath === '/reconcile' || cleanPath === '/reconciliation' || cleanPath === '/ai-reconciler' || cleanPath === '/bank-reconciliation') {
      return '/reconcile';
    }

    return cleanPath;
  };

  // Custom router state
  const [currentPath, setCurrentPath] = useState(getNormalizedPath);

  // User Margin Threshold
  const [marginThreshold, setMarginThreshold] = useState<number>(() => {
    try {
      const saved = typeof window !== 'undefined' && window.localStorage ? localStorage.getItem('solostack_margin_threshold') : null;
      return saved ? parseFloat(saved) : 48;
    } catch {
      return 48;
    }
  });

  // Notifications
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'info' } | null>(null);

  // Show automatic temporary notification helper
  const showToast = useCallback((message: string, type: 'success' | 'info' = 'success') => {
    setNotification({ message, type });
    setTimeout(() => {
      setNotification(null);
    }, 4000);
  }, []);

  // Global Privacy Mode State
  const [isPrivacyMode, setIsPrivacyMode] = useState<boolean>(() => {
    try {
      const saved = typeof window !== 'undefined' && window.localStorage ? localStorage.getItem('solostack_privacy_mode') : null;
      return saved === 'true';
    } catch {
      return false;
    }
  });

  const togglePrivacyMode = useCallback(() => {
    setIsPrivacyMode(prev => {
      const next = !prev;
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          localStorage.setItem('solostack_privacy_mode', next.toString());
        }
      } catch {}
      showToast(next ? '🔒 Privacy Mode activated (financial figures blurred)' : '👁️ Privacy Mode disabled');
      return next;
    });
  }, [showToast]);

  // Track the last real-time margin percentage change
  const [lastMarginChange, setLastMarginChange] = useState<MarginImpact | null>(null);

  // Modal States
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [isExpenseOpen, setIsExpenseOpen] = useState(false);
  const [isInsightOpen, setIsInsightOpen] = useState(false);
  
  // CSV Import States
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [importType, setImportType] = useState<'bookings' | 'expenses'>('bookings');
  
  // AI Reconciliation Suite State
  const [isReconcileModalOpen, setIsReconcileModalOpen] = useState(false);

  // Pre-filled Insight properties
  const [insightPrefilledType, setInsightPrefilledType] = useState<LinkedType>('general');
  const [insightPrefilledId, setInsightPrefilledId] = useState('');

  // Soft Reset Confirmation state
  const [confirmingReset, setConfirmingReset] = useState(false);

  // Navigation Dropdown state
  const [isNavDropdownOpen, setIsNavDropdownOpen] = useState(false);
  const [isCohortModalOpen, setIsCohortModalOpen] = useState(false);

  // Auth & Cloud Vault State
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [localUserEmail, setLocalUserEmail] = useState<string | null>(() => {
    try {
      return typeof window !== 'undefined' && window.localStorage ? localStorage.getItem('solostack_local_user_email') : null;
    } catch {
      return null;
    }
  });

  const activeUser = useMemo(() => {
    if (currentUser) return currentUser;
    if (localUserEmail) {
      return {
        email: localUserEmail,
        uid: 'local_' + localUserEmail,
        displayName: localUserEmail.split('@')[0]
      } as User;
    }
    return null;
  }, [currentUser, localUserEmail]);

  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authModalInitialEmail, setAuthModalInitialEmail] = useState<string>('');
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);
  const [isSecurityModalOpen, setIsSecurityModalOpen] = useState(false);
  const [isFAQModalOpen, setIsFAQModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [isRefundPolicyModalOpen, setIsRefundPolicyModalOpen] = useState(false);
  const [isWhatIsAzuraOSModalOpen, setIsWhatIsAzuraOSModalOpen] = useState(false);
  const [isAdvisoryOpen, setIsAdvisoryOpen] = useState(false);
  const [isConciergeOpen, setIsConciergeOpen] = useState(false);
  const [isInteractiveTourActive, setIsInteractiveTourActive] = useState(false);
  const [isVoiceModalOpen, setIsVoiceModalOpen] = useState(false);
  const [voiceModalInitialMode, setVoiceModalInitialMode] = useState<'transcribe' | 'voice_expense' | 'voice_invoice' | 'meeting_summary'>('voice_expense');
  const isVoiceEnabled = useVoiceFeatureFlag();

  // Founder Referral Invite State
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isInviteNudgeOpen, setIsInviteNudgeOpen] = useState(false);

  // Workspace Mode (Quick Clarity vs Power OS)
  const [workspaceMode, setWorkspaceMode] = useState<'quick' | 'power'>(() => {
    try {
      return (localStorage.getItem('azura_workspace_mode') as 'quick' | 'power') || 'quick';
    } catch {
      return 'quick';
    }
  });
  const [isQuickStartOpen, setIsQuickStartOpen] = useState(false);

  const handleSetWorkspaceMode = (mode: 'quick' | 'power') => {
    setWorkspaceMode(mode);
    try {
      localStorage.setItem('azura_workspace_mode', mode);
    } catch {}
    showToast(mode === 'quick' ? '⚡ Quick Clarity Mode active' : '🚀 Power OS Mode active', 'info');
  };

  // Online / Offline Cloud Vault status tracking
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);

  // Trial & Subscription Status Tracking
  const [subData, setSubData] = useState(() => getSubscriptionData());
  const trialStatus = useMemo(() => getUserTrialStatus(activeUser?.email), [activeUser?.email, subData]);

  useEffect(() => {
    const handleSubUpdate = () => {
      setSubData(getSubscriptionData());
    };

    window.addEventListener('subscription_data_updated', handleSubUpdate);
    window.addEventListener('storage', handleSubUpdate);
    return () => {
      window.removeEventListener('subscription_data_updated', handleSubUpdate);
      window.removeEventListener('storage', handleSubUpdate);
    };
  }, []);

  const isSubscribed = Boolean(
    trialStatus.isSubscribed || 
    trialStatus.isOwner || 
    (subData.status === 'active' && activeUser?.email && subData.billingEmail?.toLowerCase() === activeUser.email.toLowerCase())
  );

  const isPublicOrConceptRoute = Boolean(
    currentPath === '/' ||
    currentPath === '/what-is-azuraos' ||
    currentPath === '/architecture' ||
    currentPath === '/architecture.html' ||
    currentPath === '/about' ||
    currentPath === '/book' ||
    currentPath === '/terms' ||
    currentPath === '/privacy' ||
    currentPath === '/refunds' ||
    currentPath === '/security' ||
    currentPath === '/faq' ||
    currentPath === '/contact'
  );

  const isHardPaywallActive = Boolean(
    activeUser && 
    !isSubscribed && 
    trialStatus.isTrialExpired &&
    !isPublicOrConceptRoute
  );

  const handleLogout = async () => {
    setIsNavDropdownOpen(false);
    const prevEmail = currentUser?.email || localUserEmail;
    setLocalUserEmail(null);
    localStorage.removeItem('solostack_local_user_email');
    localStorage.removeItem('azuraos_subscription_data');
    localStorage.removeItem('solostack_business_settings');
    if (prevEmail) {
      localStorage.removeItem(`azuraos_sub_data_${prevEmail.toLowerCase()}`);
    }
    try {
      await firebaseSignOut(auth);
    } catch (err) {
      console.warn('Firebase signout note:', err);
    }
    setCurrentUser(null);
    showToast('Successfully logged out. System access locked.', 'info');
    navigate('/');
  };

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Sync state to local storage when state changes

  // Listen to Firebase Auth state changes & Cloud Vault realtime sync
  useEffect(() => {
    let unsubscribeVault: (() => void) | null = null;

    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        // Fetch existing cloud vault first so we restore their saved business settings & data
        try {
          const vault = await fetchCloudVault(user.uid);
          if (vault && (vault.dbState || vault.businessSettings)) {
            if (vault.dbState) {
              saveDb(vault.dbState);
              setState(vault.dbState);
            }
            if (vault.businessSettings) {
              try {
                localStorage.setItem('solostack_business_settings', JSON.stringify(vault.businessSettings));
                window.dispatchEvent(new CustomEvent('businessSettingsUpdated', { detail: vault.businessSettings }));
              } catch (e) {
                console.error('Failed to set local business settings from vault:', e);
              }
            }
            if (vault.updatedAt) {
              setLastSyncTimestamp(vault.updatedAt);
            }
          } else {
            // First time this user logs in with an empty vault: upload initial state
            syncStateToCloud(user.uid, getDbState(), getBusinessSettings());
          }
        } catch (err) {
          console.warn('Error during initial cloud vault fetch on login:', err);
        }

        // Subscribe to real-time Cloud Vault changes
        unsubscribeVault = subscribeToCloudVault(user.uid, (vaultData) => {
          if (vaultData && vaultData.dbState) {
            setState(vaultData.dbState);
          }
        });
      } else {
        if (unsubscribeVault) {
          unsubscribeVault();
          unsubscribeVault = null;
        }
      }
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeVault) unsubscribeVault();
    };
  }, []);

  // Initialize real-time Firestore whitelist sync for multi-domain authorization
  const [, setWhitelistTick] = useState(0);
  useEffect(() => {
    const unsubscribeWhitelist = initAllowedUsersSync(() => {
      setWhitelistTick((t) => t + 1);
    });
    return unsubscribeWhitelist;
  }, []);

  // Initialize background sync queue flush listeners for offline/online resilience
  useEffect(() => {
    const cleanup = initBackgroundSyncListeners(
      () => currentUser?.uid,
      () => {
        showToast('Cloud Vault synchronized automatically after connection restored!', 'success');
      }
    );
    return cleanup;
  }, [currentUser?.uid]);

  // Sync state helper function
  const persistState = (newState: any) => {
    saveDb(newState);
    setState({ ...newState });
    if (currentUser) {
      syncStateToCloud(currentUser.uid, newState);
    }
  };

  // Listen for Dodo Payments success redirect across all routes & auto-sync active subscription
  useEffect(() => {
    const handlePaymentReturn = async () => {
      try {
        const searchParams = new URLSearchParams(window.location.search);
        const hashParams = new URLSearchParams(window.location.hash.replace(/^#.*\?/, ''));
        
        const paymentStatus = searchParams.get('payment_status') || hashParams.get('payment_status') || searchParams.get('status');
        const hasPaymentParams = paymentStatus === 'success' || paymentStatus === 'succeeded' || !!searchParams.get('payment_id') || !!searchParams.get('subscription_id');

        if (hasPaymentParams) {
          // Clean query params immediately so links cannot be copied or shared to activate other users
          window.history.replaceState({}, '', window.location.pathname);

          const userEmail = searchParams.get('email') || hashParams.get('email') || auth.currentUser?.email || localStorage.getItem('solostack_local_user_email');
          if (userEmail) {
            // Verify with Dodo backend
            const res = await fetch('/api/payments/sync-subscription', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ customerEmail: userEmail })
            });
            const data = await res.json().catch(() => null);
            if (data && data.success && data.isSubscribed && data.subscription) {
              activateUserSubscription(userEmail, data.subscription.planId || 'pro', data.subscription.billingCycle || 'monthly');
              showToast('🎉 Subscription confirmed! Sovereign Founder Pro is active.', 'success');
              if (window.location.pathname !== '/billing' && window.location.pathname !== '/welcome') {
                navigate('/billing');
              }
            } else {
              showToast('ℹ️ Workspace loaded: Standard founder evaluation active.', 'info');
            }
          }
        }
      } catch (e) {
        console.warn('Payment return check notice:', e);
      }
    };

    handlePaymentReturn();

    // Verify active subscription with Dodo backend in background & revoke false local state
    const verifySubscriptionWithDodo = async () => {
      try {
        const email = auth.currentUser?.email || localStorage.getItem('solostack_local_user_email');
        if (!email) return;
        const res = await fetch('/api/payments/sync-subscription', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ customerEmail: email })
        });
        const contentType = res.headers.get('content-type') || '';
        if (!contentType.includes('application/json')) return;
        const data = await res.json().catch(() => null);
        if (data && data.success) {
          if (data.isSubscribed && data.subscription) {
            activateUserSubscription(email, data.subscription.planId || 'pro', data.subscription.billingCycle || 'monthly');
          } else if (!data.isSubscribed) {
            // User does NOT have an active subscription on Dodo Payments
            deactivateUserSubscription(email);
          }
        }
      } catch (e) {
        // Silent background sync
      }
    };

    verifySubscriptionWithDodo();
  }, [currentUser?.email]);

  // Sync with browser URL pathname and hash changes
  useEffect(() => {
    const handleUrlChange = () => {
      const path = getNormalizedPath();
      setCurrentPath(path);
      if (path.includes('privacy')) setIsPrivacyModalOpen(true);
      if (path.includes('terms')) setIsTermsModalOpen(true);
      if (path.includes('refund')) setIsRefundPolicyModalOpen(true);
      if (path.includes('security') || path.includes('architecture')) setIsSecurityModalOpen(true);
      if (path.includes('faq')) setIsFAQModalOpen(true);
      if (!path.startsWith('/ai-assistant') && (path === '/pitch-deck' || path === '/grindstone' || path === '/one-pager' || path === '/dealership' || path === '/motor-pilot')) {
        setCurrentPath('/admin');
      }
    };

    // Auto-trigger legal modals on initial load if route matches
    const initialPath = getNormalizedPath();
    if (initialPath.includes('privacy')) setIsPrivacyModalOpen(true);
    if (initialPath.includes('terms')) setIsTermsModalOpen(true);
    if (initialPath.includes('refund')) setIsRefundPolicyModalOpen(true);
    if (initialPath.includes('security') || initialPath.includes('architecture')) setIsSecurityModalOpen(true);
    if (initialPath.includes('faq')) setIsFAQModalOpen(true);
    if (!initialPath.startsWith('/ai-assistant') && (initialPath === '/pitch-deck' || initialPath === '/grindstone' || initialPath === '/one-pager' || initialPath === '/dealership' || initialPath === '/motor-pilot')) {
      setCurrentPath('/admin');
    }

    window.addEventListener('popstate', handleUrlChange);
    window.addEventListener('hashchange', handleUrlChange);
    return () => {
      window.removeEventListener('popstate', handleUrlChange);
      window.removeEventListener('hashchange', handleUrlChange);
    };
  }, []);

  // Safe navigation function
  const navigate = useCallback((path: string) => {
    window.history.pushState({}, '', path);
    setCurrentPath(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (path.includes('privacy')) setIsPrivacyModalOpen(true);
    if (path.includes('terms')) setIsTermsModalOpen(true);
    if (path.includes('refund')) setIsRefundPolicyModalOpen(true);
    if (path.includes('security') || path.includes('architecture')) setIsSecurityModalOpen(true);
    if (path.includes('faq')) setIsFAQModalOpen(true);
    if (path === '/what-is-azuraos' || path.startsWith('/what-is-azuraos')) setIsWhatIsAzuraOSModalOpen(true);
    if (path.includes('contact') || path.includes('partnership')) setIsContactModalOpen(true);
    if (!path.startsWith('/ai-assistant') && (path === '/pitch-deck' || path === '/grindstone' || path === '/one-pager' || path === '/dealership' || path === '/motor-pilot')) {
      setCurrentPath('/admin');
    }
  }, []);

  const handleOpenReconciler = useCallback(() => {
    navigate('/ai-assistant?pillar=reconciler');
  }, [navigate]);

  // Core Profitability tracker helper
  const trackMarginImpact = (
    action: () => void, 
    type: 'booking' | 'expense' | 'delete_booking' | 'delete_expense', 
    itemName: string, 
    amount: number
  ) => {
    const oldMargin = calculateMargin(state.bookings, state.expenses);
    
    action();
    
    const newState = getDbState();
    const newMargin = calculateMargin(newState.bookings, newState.expenses);
    
    const crossedBelow = oldMargin >= marginThreshold && newMargin < marginThreshold;
    const crossedAbove = oldMargin < marginThreshold && newMargin >= marginThreshold;
    
    setLastMarginChange({
      type,
      itemName,
      amount,
      oldMargin,
      newMargin,
      threshold: marginThreshold,
      crossedBelow,
      crossedAbove,
      timestamp: Date.now()
    });
  };

  // Actions
  const handleCreateBooking = (data: Omit<Booking, 'id' | 'created_at'> & { insight_title?: string; insight_note?: string }) => {
    const limitCheck = checkSubscriptionLimits('booking', state.bookings.length, activeUser?.email);
    if (!limitCheck.allowed) {
      showToast(limitCheck.message || 'Free tier limit reached (10 entries max). Upgrade to Sovereign Founder Pro for unlimited entries!');
      navigate('/billing');
      return;
    }

    const { insight_title, insight_note, ...bookingData } = data;
    trackMarginImpact(() => {
      const newB = addBooking(bookingData);
      if (insight_title && insight_note) {
        addInsight({
          title: insight_title,
          note: insight_note,
          linked_type: 'booking',
          linked_id: newB.id
        });
      }
    }, 'booking', `Booking for ${data.client_name}`, data.price);

    persistState(getDbState());
    showToast(`Successfully created booking for ${data.client_name}!`);
    navigate('/bookings');
  };

  const handlePublicBooking = (data: Omit<Booking, 'id' | 'created_at'>): Booking => {
    let newB: Booking = { id: '', type: '', price: 0, client_name: '', date: '', created_at: '' };
    trackMarginImpact(() => {
      newB = addBooking(data);
    }, 'booking', `Public Client Booking (${data.client_name})`, data.price);

    persistState(getDbState());
    return newB;
  };

  const handleCreateExpense = (data: Omit<Expense, 'id' | 'created_at'> & { insight_title?: string; insight_note?: string }) => {
    const limitCheck = checkSubscriptionLimits('expense', state.expenses.length, activeUser?.email);
    if (!limitCheck.allowed) {
      showToast(limitCheck.message || 'Free tier limit reached (10 entries max). Upgrade to Sovereign Founder Pro for unlimited entries!');
      navigate('/billing');
      return;
    }

    const { insight_title, insight_note, ...expenseData } = data;
    trackMarginImpact(() => {
      const newE = addExpense(expenseData);
      if (insight_title && insight_note) {
        addInsight({
          title: insight_title,
          note: insight_note,
          linked_type: 'expense',
          linked_id: newE.id
        });
      }
    }, 'expense', data.note, data.amount);

    persistState(getDbState());
    showToast(`Successfully recorded R${data.amount} expense!`);
    navigate('/expenses');
  };

  const handleCreateInsight = (data: Omit<Insight, 'id' | 'created_at'>) => {
    const newI = addInsight(data);
    persistState(getDbState());
    showToast(`J-Lens Insight "${newI.title}" securely archived!`);
  };

  const handleQuickAddBooking = (price: number, type: ServiceType, clientName: string) => {
    const limitCheck = checkSubscriptionLimits('booking', state.bookings.length, activeUser?.email);
    if (!limitCheck.allowed) {
      showToast(limitCheck.message || 'Free tier limit reached (10 entries max). Upgrade to Sovereign Founder Pro for unlimited entries!');
      navigate('/billing');
      return;
    }

    trackMarginImpact(() => {
      addBooking({
        price,
        type,
        client_name: clientName,
        date: new Date().toISOString()
      });
    }, 'booking', `Quick Booking (${clientName})`, price);

    persistState(getDbState());
    showToast(`Quick booking of R${price} recorded for ${clientName}!`);
  };

  const handleQuickAddExpense = (amount: number, category: ExpenseCategory, linkedStream: ServiceType, note: string) => {
    const limitCheck = checkSubscriptionLimits('expense', state.expenses.length, activeUser?.email);
    if (!limitCheck.allowed) {
      showToast(limitCheck.message || 'Free tier limit reached (10 entries max). Upgrade to Sovereign Founder Pro for unlimited entries!');
      navigate('/billing');
      return;
    }

    trackMarginImpact(() => {
      addExpense({
        amount,
        category,
        linked_stream: linkedStream,
        note,
        date: new Date().toISOString()
      });
    }, 'expense', note, amount);

    persistState(getDbState());
    showToast(`Quick expense of R${amount} recorded: "${note}"`);
  };

  const handleDeleteBooking = (id: string) => {
    if (confirm('Are you sure you want to delete this booking? Linked J-Lens insights will be unlinked.')) {
      const b = state.bookings.find(x => x.id === id);
      const name = b ? `Booking for ${b.client_name}` : 'Booking';
      const amount = b ? b.price : 0;
      trackMarginImpact(() => {
        deleteBooking(id);
      }, 'delete_booking', name, amount);

      persistState(getDbState());
      showToast('Booking record removed.', 'info');
    }
  };

  const handleDeleteExpense = (id: string) => {
    if (confirm('Are you sure you want to delete this expense? Linked J-Lens insights will be unlinked.')) {
      const e = state.expenses.find(x => x.id === id);
      const name = e ? e.note : 'Expense';
      const amount = e ? e.amount : 0;
      trackMarginImpact(() => {
        deleteExpense(id);
      }, 'delete_expense', name, amount);

      persistState(getDbState());
      showToast('Expense record removed.', 'info');
    }
  };

  const handleDeleteInsight = (id: string) => {
    if (confirm('Are you sure you want to discard this J-Lens insight?')) {
      deleteInsight(id);
      persistState(getDbState());
      showToast('Insight removed from vault.', 'info');
    }
  };

  const handleUpdateBookingStatus = (id: string, status: 'paid' | 'unpaid' | 'cancelled') => {
    updateBookingStatus(id, status);
    persistState(getDbState());
    if (status === 'paid') {
      showToast('Invoice marked as Paid ✓', 'success');
    } else if (status === 'cancelled') {
      showToast('Invoice marked as Cancelled / Void (R0 revenue)', 'info');
    } else {
      showToast('Invoice marked as Unpaid', 'info');
    }
  };

  const handleAddStream = (name: string) => {
    const clean = name.trim();
    if (!clean) return;
    if (state.streams && state.streams.some(s => s.toLowerCase() === clean.toLowerCase())) {
      showToast('This income stream already exists.', 'info');
      return;
    }
    addStream(clean);
    persistState(getDbState());
    showToast(`Income stream "${clean}" successfully added!`);
  };

  const handleRemoveStream = (name: string) => {
    if (confirm(`Are you sure you want to delete the stream "${name}"? Deleting a stream will automatically remove ALL of its associated bookings and expenses!`)) {
      removeStream(name);
      persistState(getDbState());
      showToast(`Income stream "${name}" and all its records have been removed.`, 'info');
    }
  };

  // Helper to trigger Insight modal with custom presets
  const triggerAddInsight = (prefilledType: LinkedType = 'general', prefilledId: string = '') => {
    setInsightPrefilledType(prefilledType);
    setInsightPrefilledId(prefilledId);
    setIsInsightOpen(true);
  };

  const handleImportComplete = (importedData: any[], newStreamsAdded: string[]) => {
    // 1. If any new streams were added, append them to streams
    let updatedStreams = [...(state.streams || [])];
    let streamsChanged = false;
    newStreamsAdded.forEach(ns => {
      if (!updatedStreams.includes(ns)) {
        updatedStreams.push(ns);
        streamsChanged = true;
      }
    });

    // 2. Add each imported item to DB state
    const currentDbState = getDbState();
    
    if (streamsChanged) {
      currentDbState.streams = updatedStreams;
    }

    if (importType === 'bookings') {
      importedData.forEach(item => {
        const newBooking = {
          ...item,
          id: 'b-' + Math.random().toString(36).substr(2, 9),
          created_at: new Date().toISOString()
        };
        currentDbState.bookings.unshift(newBooking);
      });
    } else {
      importedData.forEach(item => {
        const newExpense = {
          ...item,
          id: 'e-' + Math.random().toString(36).substr(2, 9),
          created_at: new Date().toISOString()
        };
        currentDbState.expenses.unshift(newExpense);
      });
    }

    // Save and update state
    saveDb(currentDbState);
    setState(currentDbState);

    // Calculate new margin and show standard real-time feedback
    const oldMargin = marginVal;
    const newMargin = calculateMargin(currentDbState.bookings, currentDbState.expenses);
    const crossedBelow = oldMargin >= marginThreshold && newMargin < marginThreshold;
    const crossedAbove = oldMargin < marginThreshold && newMargin >= marginThreshold;

    setLastMarginChange({
      type: importType === 'bookings' ? 'booking' : 'expense',
      itemName: `CSV Import (${importedData.length} items)`,
      amount: importedData.reduce((sum, item) => sum + (importType === 'bookings' ? item.price : item.amount), 0),
      oldMargin,
      newMargin,
      threshold: marginThreshold,
      crossedBelow,
      crossedAbove,
      timestamp: Date.now()
    });

    recordBackupAction();
    showToast(`Successfully imported ${importedData.length} records!`, 'success');
  };

  const isCohortPath = 
    currentPath === '/cohorts' || 
    currentPath === '/accelerators' || 
    currentPath === '/incubators' || 
    currentPath === '/enterprise';

  const isLandingOrConcept = 
    currentPath === '/' || 
    currentPath === '/concept' || 
    currentPath === '/about' ||
    isCohortPath ||
    currentPath.includes('privacy') ||
    currentPath.includes('terms') ||
    currentPath.includes('refund') ||
    currentPath.includes('security') ||
    currentPath.includes('architecture') ||
    currentPath.includes('faq');

  // Determine current active page
  const renderActiveView = () => {
    if (currentPath === '/book') {
      return (
        <PublicBookingView
          streams={state.streams || ['consult', 'retreat', 'coffee']}
          onBookingConfirmed={handlePublicBooking}
          showToast={showToast}
          onNavigateHome={() => navigate('/dashboard')}
        />
      );
    }

    if (isLandingOrConcept) {
      return (
        <LandingView
          initialAudienceMode={isCohortPath ? 'cohort' : undefined}
          onGetStarted={() => {
            if (workspaceMode === 'quick') {
              navigate('/dashboard');
            } else {
              navigate('/welcome');
            }
          }}
          onOpenQuickStart={() => setIsQuickStartOpen(true)}
          marginThreshold={marginThreshold}
          onOpenAuthModal={(email) => {
            setAuthModalInitialEmail(email || '');
            setIsAuthModalOpen(true);
          }}
          currentUser={currentUser}
          onOpenInviteModal={() => setIsInviteModalOpen(true)}
          onOpenTerms={() => setIsTermsModalOpen(true)}
          onOpenPrivacy={() => setIsPrivacyModalOpen(true)}
          onOpenSecurity={() => setIsSecurityModalOpen(true)}
          onOpenFAQ={() => setIsFAQModalOpen(true)}
          onOpenRefundPolicy={() => setIsRefundPolicyModalOpen(true)}
          onOpenContact={() => setIsContactModalOpen(true)}
          onOpenWhatIsAzuraOS={() => setIsWhatIsAzuraOSModalOpen(true)}
          onOpenReconciler={handleOpenReconciler}
          onOpenCohortPortal={() => navigate('/cohort-portal')}
          showToast={showToast}
          onTourStateChange={(open) => setIsInteractiveTourActive(open)}
          onLoadDemo={() => {
            const loaded = preloadDemo();
            setState(loaded);
            showToast('🚀 Demo dataset loaded! Explore realistic bookings, expenses, and insights.', 'success');
            navigate('/dashboard');
          }}
          onClearSandbox={() => {
            const cleared = clearAllData();
            setState(cleared);
            showToast('Sandbox reset to zero. Ready for your custom data!', 'info');
            navigate('/dashboard');
          }}
        />
      );
    }

    if (currentPath === '/calendar') {
      return (
        <CalendarManager
          bookings={state.bookings}
          showToast={showToast}
          currentUser={currentUser}
        />
      );
    }

    if (currentPath === '/j-lens') {
      return (
        <PaywallGuard
          feature="analytics"
          featureName="J-Lens Advanced Cash Flow Analytics"
          featureDescription="Deep revenue & expense analysis, profit margin forecasting, and strategic financial insights are exclusively unlocked on Sovereign Founder Pro."
          currentUserEmail={activeUser?.email || undefined}
          onNavigate={navigate}
        >
          <JLensList
            insights={state.insights}
            bookings={state.bookings}
            expenses={state.expenses}
            onDeleteInsight={handleDeleteInsight}
            onAddInsightClick={() => triggerAddInsight('general', '')}
          />
        </PaywallGuard>
      );
    }

    const hasNoStreams = !state.streams || state.streams.length === 0;
    const basePath = currentPath.split('?')[0];

    switch (basePath) {
      case '/ai-assistant':
        return (
          <AICofounderAssistantView
            bookings={state.bookings}
            expenses={state.expenses}
            streams={state.streams || []}
            showToast={showToast}
            onNavigate={navigate}
            currentUser={currentUser}
            onCreateBooking={handleCreateBooking}
            initialPillar={
              currentPath.includes('pillar=reconciler') ? 'reconciler' :
              currentPath.includes('pillar=legal') ? 'legal' :
              currentPath.includes('pillar=ops') ? 'ops' :
              currentPath.includes('pillar=pitch') ? 'pitch' :
              undefined
            }
            onReconcileSuccess={(summary) => {
              setState(getDbState());
              showToast(`✨ Reconciled ${summary.bookingsAdded} bookings & ${summary.expensesAdded} expenses!`, 'success');
            }}
            isPrivacyMode={isPrivacyMode}
          />
        );
      case '/focus':
        return (
          <FocusRoadmapView
            bookings={state.bookings}
            onNavigate={navigate}
            showToast={showToast}
          />
        );
      case '/invoices':
        return (
          <InvoicesList
            bookings={state.bookings}
            onUpdateBookingStatus={handleUpdateBookingStatus}
            onNavigate={navigate}
            onAddBookingClick={() => setIsBookingOpen(true)}
            isPrivacyMode={isPrivacyMode}
            onTogglePrivacyMode={togglePrivacyMode}
          />
        );
      case '/bookings':
        if (hasNoStreams) {
          return (
            <div className="space-y-6 animate-fade-in max-w-xl mx-auto py-12 text-center">
              <div className="p-6 bg-[#151819] border border-[#1F2426] rounded-2xl shadow-xl space-y-6">
                <div className="w-12 h-12 bg-[#14B8A6]/10 rounded-full flex items-center justify-center text-[#14B8A6] mx-auto">
                  <Calendar size={22} />
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-display font-semibold text-white">Setup Streams First</h3>
                  <p className="text-xs text-gray-400 leading-relaxed">
                    You need to define at least one custom income stream before you can record client bookings.
                  </p>
                </div>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="px-6 h-11 bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-bold rounded-xl transition-all cursor-pointer text-xs"
                >
                  Configure Streams on Dashboard
                </button>
              </div>
            </div>
          );
         }
        return (
          <BookingsList
            bookings={state.bookings}
            onAddBookingClick={() => setIsBookingOpen(true)}
            onDeleteBooking={handleDeleteBooking}
            onAddInsightClick={triggerAddInsight}
            onUpdateBookingStatus={handleUpdateBookingStatus}
            onNavigate={navigate}
            onImportCSVClick={() => {
              setImportType('bookings');
              setIsImportOpen(true);
            }}
            onOpenReconciler={handleOpenReconciler}
            isPrivacyMode={isPrivacyMode}
          />
        );
      case '/expenses':
        if (hasNoStreams) {
          return (
            <div className="space-y-6 animate-fade-in max-w-xl mx-auto py-12 text-center">
              <div className="p-6 bg-[#151819] border border-[#1F2426] rounded-2xl shadow-xl space-y-6">
                <div className="w-12 h-12 bg-rose-500/10 rounded-full flex items-center justify-center text-rose-400 mx-auto">
                  <DollarSign size={22} />
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-display font-semibold text-white">Setup Streams First</h3>
                  <p className="text-xs text-gray-400 leading-relaxed">
                    You need to define at least one custom income stream before you can log operational costs.
                  </p>
                </div>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="px-6 h-11 bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-bold rounded-xl transition-all cursor-pointer text-xs"
                >
                  Configure Streams on Dashboard
                </button>
              </div>
            </div>
          );
        }
        return (
          <ExpensesList
            expenses={state.expenses}
            onAddExpenseClick={() => setIsExpenseOpen(true)}
            onDeleteExpense={handleDeleteExpense}
            onAddInsightClick={triggerAddInsight}
            onImportCSVClick={() => {
              setImportType('expenses');
              setIsImportOpen(true);
            }}
            onOpenReconciler={handleOpenReconciler}
            isPrivacyMode={isPrivacyMode}
          />
        );
      case '/reconcile':
        return (
          <AICofounderAssistantView
            bookings={state.bookings}
            expenses={state.expenses}
            streams={state.streams || []}
            showToast={showToast}
            onNavigate={navigate}
            currentUser={currentUser}
            onCreateBooking={handleCreateBooking}
            initialPillar="reconciler"
            onReconcileSuccess={(summary) => {
              setState(getDbState());
              showToast(`✨ Reconciled ${summary.bookingsAdded} bookings & ${summary.expensesAdded} expenses!`, 'success');
            }}
            isPrivacyMode={isPrivacyMode}
          />
        );
      case '/welcome':
        return (
          <WelcomeAboardView
            onNavigate={navigate}
            showToast={showToast}
            onOpenConcierge={() => setIsConciergeOpen(true)}
            onLoadDemo={() => {
              const loaded = preloadDemo();
              setState(loaded);
              showToast('🚀 Demo dataset loaded! Explore realistic bookings, expenses, and insights.', 'success');
              navigate('/dashboard');
            }}
            onClearSandbox={() => {
              const cleared = clearAllData();
              setState(cleared);
              showToast('Sandbox reset to zero. Ready for your custom data!', 'info');
              navigate('/dashboard');
            }}
          />
        );
      case '/billing':
        return (
          <div className="space-y-6 animate-fade-in max-w-5xl mx-auto py-4">
            <SubscriptionBillingView
              showToast={showToast}
              onRefreshAuditLogs={() => {}}
              onNavigate={navigate}
            />
          </div>
        );
      case '/cohort-portal':
      case '/cohort-pilot':
      case '/cohort-onboarding':
      case '/sublicense':
        return (
          <CohortPilotPortalView
            onNavigate={navigate}
            showToast={showToast}
          />
        );
      case '/settings':
        return (
          <BusinessHQView
            bookings={state.bookings}
            expenses={state.expenses}
            insights={state.insights}
            streams={state.streams}
            threshold={marginThreshold}
            isPrivacyMode={isPrivacyMode}
            onTogglePrivacyMode={togglePrivacyMode}
            showToast={showToast}
            currentUser={activeUser}
            onNavigate={navigate}
            onOpenReconciler={handleOpenReconciler}
          />
        );
      case '/admin':
        return (
          <BusinessHQView
            bookings={state.bookings}
            expenses={state.expenses}
            insights={state.insights}
            streams={state.streams}
            threshold={marginThreshold}
            isPrivacyMode={isPrivacyMode}
            onTogglePrivacyMode={togglePrivacyMode}
            showToast={showToast}
            currentUser={activeUser}
            onNavigate={navigate}
            initialSubTab="super_admin"
            onOpenReconciler={handleOpenReconciler}
          />
        );
      case '/app':
      case '/dashboard':
      default:
        if (workspaceMode === 'quick') {
          return (
            <QuickClarityDashboard
              onSwitchToPowerOS={() => handleSetWorkspaceMode('power')}
              onOpenReconciler={handleOpenReconciler}
              onOpenInvoices={() => navigate('/invoices')}
              showToast={showToast}
            />
          );
        }

        if (hasNoStreams) {
          return (
            <OnboardingView
              onComplete={(streams) => {
                const newState = {
                  bookings: [],
                  expenses: [],
                  insights: [],
                  streams
                };
                saveDb(newState);
                setState(newState);
                showToast('Sandbox launched! Add your first booking or expense below.');
                navigate('/dashboard');
              }}
              onLoadDemo={() => {
                const loaded = preloadDemo();
                setState(loaded);
                showToast('Demo data loaded successfully! Explore the sandbox.');
                navigate('/dashboard');
              }}
              onOpenReconciler={handleOpenReconciler}
              showToast={showToast}
            />
          );
        }
        return (
          <div className="space-y-8 animate-fade-in">
            <Dashboard
              bookings={state.bookings}
              expenses={state.expenses}
              insights={state.insights}
              streams={state.streams}
              onAddBookingClick={() => setIsBookingOpen(true)}
              onAddExpenseClick={() => setIsExpenseOpen(true)}
              onAddInsightClick={triggerAddInsight}
              onDeleteBooking={handleDeleteBooking}
              onDeleteExpense={handleDeleteExpense}
              onNavigate={navigate}
              onQuickAddBooking={handleQuickAddBooking}
              onQuickAddExpense={handleQuickAddExpense}
              marginThreshold={marginThreshold}
              onAddStream={handleAddStream}
              onRemoveStream={handleRemoveStream}
              isPrivacyMode={isPrivacyMode}
              onOpenInviteModal={() => setIsInviteModalOpen(true)}
              onLoadDemoData={() => {
                const loaded = preloadDemo();
                setState(loaded);
                showToast('🚀 Sample demo dataset loaded! Explore calculations & features.', 'success');
              }}
              onClearDemoData={() => {
                const cleared = clearAllData();
                setState(cleared);
                showToast('Workspace cleared to 0! You are now tracking your real business.', 'info');
              }}
              onOpenReconciler={handleOpenReconciler}
            />
            <ExportCenter
              bookings={state.bookings}
              expenses={state.expenses}
              insights={state.insights}
              streams={state.streams}
              threshold={marginThreshold}
              onOpenReconciler={handleOpenReconciler}
            />
          </div>
        );
    }
  };


  const marginVal = calculateMargin(state.bookings, state.expenses);
  const statusColors = getMarginStatus(marginVal, marginThreshold);

  const activeBookings = (state.bookings || []).filter(b => b.status !== 'cancelled');
  const totalRevenue = activeBookings.reduce((sum, b) => sum + (Number(b.price) || 0), 0);
  const totalExpenses = (state.expenses || []).reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
  const netProfit = totalRevenue - totalExpenses;

  const isConceptActive = 
    currentPath === '/' || 
    currentPath === '/concept' || 
    currentPath === '/about' ||
    isCohortPath;
  const isWhatIsAzuraOSActive = currentPath === '/what-is-azuraos';
  const isAppActive = !isConceptActive && !isWhatIsAzuraOSActive;

  // Dynamic SEO metadata calculation for production search indexing & social crawlers
  const getSeoMetadata = () => {
    if (isTermsModalOpen) {
      return getSeoConfigForPath('/terms');
    }
    if (isPrivacyModalOpen) {
      return getSeoConfigForPath('/privacy');
    }
    if (isFAQModalOpen) {
      return getSeoConfigForPath('/faq');
    }
    return getSeoConfigForPath(currentPath);
  };

  const seoData = getSeoMetadata();

  // If on public explanation route (/what-is-azuraos), render WhatIsAzuraOSView directly
  if (currentPath === '/what-is-azuraos') {
    return (
      <>
        <SEO title={seoData.title} description={seoData.description} path={seoData.path} image={seoData.image} />
        <WhatIsAzuraOSView navigate={navigate} showToast={showToast} />
      </>
    );
  }

  // If on public booking route, render PublicBookingView directly as a full-page client portal
  if (currentPath === '/book') {
    return (
      <>
        <SEO title={seoData.title} description={seoData.description} path={seoData.path} image={seoData.image} />
        <PublicBookingView
          streams={state.streams || ['consult', 'retreat', 'coffee']}
          onBookingConfirmed={handlePublicBooking}
          showToast={showToast}
          onNavigateHome={() => navigate('/dashboard')}
        />
      </>
    );
  }

  // 🛡️ SOVEREIGN SECURITY GATE: Protect all app/dashboard routes (/app, /dashboard, etc.)
  if (isAppActive) {
    if (!activeUser) {
      return (
        <div className="min-h-screen bg-[#0C0E0F] text-gray-200 flex flex-col justify-center items-center p-4 selection:bg-[#14B8A6]/20">
          <SEO title={seoData.title} description={seoData.description} path={seoData.path} />
          <div className="max-w-md w-full bg-[#151819] border border-[#1F2426] rounded-2xl p-8 shadow-2xl text-center space-y-6 animate-fade-in">
            <div className="w-14 h-14 bg-[#14B8A6]/10 border border-[#14B8A6]/20 rounded-2xl flex items-center justify-center text-[#14B8A6] mx-auto shadow-inner">
              <Building2 size={28} />
            </div>
            <div className="space-y-2">
              <span className="text-[10px] font-mono font-bold text-[#14B8A6] uppercase tracking-widest bg-[#14B8A6]/10 px-2.5 py-1 rounded-md border border-[#14B8A6]/20">
                7-Day Free Trial
              </span>
              <h2 className="font-display font-bold text-2xl text-white tracking-tight pt-2">
                Sign in or Create Account
              </h2>
              <p className="text-xs text-gray-400 leading-relaxed">
                Access your AzuraOS workspace to manage your financials, client invoices, and AI Co-Founder insights with a full 7-Day Free Trial.
              </p>
            </div>
            <div className="pt-2 space-y-3">
              <button
                onClick={() => setIsAuthModalOpen(true)}
                className="w-full py-3.5 px-4 bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-bold rounded-xl transition-all shadow-lg hover:shadow-[#14B8A6]/20 flex items-center justify-center gap-2 cursor-pointer text-sm"
              >
                <Sparkles size={16} /> Sign In / Create Account
              </button>
              <button
                onClick={() => navigate('/')}
                className="w-full py-2.5 px-4 bg-[#1F2426] hover:bg-[#2A2F31] text-gray-300 font-medium rounded-xl transition-all text-xs cursor-pointer"
              >
                ← Return to Public Landing Page
              </button>
            </div>
          </div>
          {isAuthModalOpen && (
            <AuthModal
              isOpen={isAuthModalOpen}
              initialEmail={authModalInitialEmail}
              onClose={() => {
                setIsAuthModalOpen(false);
                setAuthModalInitialEmail('');
              }}
              onSuccess={(userEmail) => {
                setLocalUserEmail(userEmail);
                localStorage.setItem('solostack_local_user_email', userEmail);
                setIsAuthModalOpen(false);
                setAuthModalInitialEmail('');
                navigate('/welcome');
              }}
              showToast={showToast}
              onOpenTerms={() => setIsTermsModalOpen(true)}
            />
          )}
        </div>
      );
    }
  }

  const isSuperAdmin = 
    isUserSuperAdmin(activeUser?.email) || 
    isUserSuperAdmin(currentUser?.email) || 
    isUserSuperAdmin(auth.currentUser?.email) || 
    (typeof window !== 'undefined' && (
      isUserSuperAdmin(localStorage.getItem('solostack_local_user_email') || '') ||
      isUserSuperAdmin(localStorage.getItem('azuraos_user_email') || '') ||
      isUserSuperAdmin(localStorage.getItem('user_email') || '')
    )) ||
    (!activeUser && !currentUser);

  const chiefOfStaffModules = [
    { path: '/ai-assistant', label: 'Chief of Staff Command', icon: Sparkles, desc: 'Morning Brief, Strategy Stress-Test & Sprints' },
    { path: '/ai-assistant?pillar=legal', label: 'Legal & Agreement Proformas', icon: Scale, desc: 'Client SOWs, Scope Defense & Kill-Fees' },
    { path: '/focus', label: 'Strategic Roadmap', icon: Target, desc: 'High-Leverage Sprints & Milestones' },
    ...(isVoiceEnabled ? [{ path: '#voice', label: 'Voice Directives & Debriefs', icon: Mic, desc: 'Native Multimodal Audio Memos (BYOK)' }] : []),
  ];

  const cfoSuiteModules = [
    { path: '/dashboard', label: 'Fractional CFO & Margins', icon: Sliders, desc: 'Live Margin Defense & Safe Spendable Draw' },
    { path: '/reconcile', label: 'AI Bank Reconciler', icon: Sparkles, desc: '0.05s Bank Statement Parser & OCR' },
    { path: '/invoices', label: 'Client Invoices & Receivables', icon: DollarSign, desc: 'Multi-Tax Proformas & Receivables Collection' },
    { path: '/bookings', label: 'Bookings & Retainers', icon: Calendar, desc: 'Active Revenue Streams & Client Calendar' },
    { path: '/expenses', label: 'Operating Costs & Taxes', icon: TrendingDown, desc: 'Deductible Outflows & Burn Control' },
    { path: '/j-lens', label: 'J-Lens Cash Flow Analytics', icon: Brain, desc: 'Cognitive Rules & Predictive Reserves' },
    { path: '/settings', label: 'Tax Architecture & HQ', icon: Building2, desc: 'VAT/Tax Rules, AES-256 Vault & Profile' },
  ];

  const appModules = [...chiefOfStaffModules, ...cfoSuiteModules];

  let activeTabLabel = t('nav.app_stack_modules');
  if (currentPath === '/welcome') activeTabLabel = t('nav.welcome');
  else if (currentPath === '/billing') activeTabLabel = t('nav.billing');
  else if (currentPath === '/dashboard' || currentPath === '/app') activeTabLabel = t('nav.dashboard');
  else if (currentPath === '/reconcile') activeTabLabel = 'AI Reconciler';
  else if (currentPath === '/focus') activeTabLabel = t('nav.focus');
  else if (currentPath === '/ai-assistant') activeTabLabel = t('nav.ai_assistant');
  else if (currentPath === '/calendar') activeTabLabel = t('nav.calendar');
  else if (currentPath === '/book') activeTabLabel = t('nav.book');
  else if (currentPath === '/bookings') activeTabLabel = t('nav.bookings');
  else if (currentPath === '/invoices') activeTabLabel = t('nav.invoices');
  else if (currentPath === '/expenses') activeTabLabel = t('nav.expenses');
  else if (currentPath === '/settings') activeTabLabel = t('nav.settings');
  else if (currentPath === '/admin') activeTabLabel = 'Admin Dashboard';
  else if (currentPath === '/j-lens') activeTabLabel = t('nav.j_lens');

  return (
    <div className="min-h-screen min-h-[100dvh] mobile-safe-layout bg-dark-bg text-gray-200 flex flex-col justify-between selection:bg-[#14B8A6]/20 selection:text-white pb-24 md:pb-8">
      <SEO title={seoData.title} description={seoData.description} path={seoData.path} image={seoData.image} />
      
      {/* 1. Sticky Navigation Header - Streamlined Minimal Anchor */}
      <header className="sticky top-0 z-50 bg-[#0C0E0F]/95 backdrop-blur-md border-b border-[#1F2426] px-3.5 sm:px-6 py-2.5 sm:py-3 shadow-md">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-3">
          
          {/* Brand Mark: AzuraOS PRO (Clean, Uncluttered, Never Covered) */}
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <div 
              onClick={() => navigate('/')} 
              onDoubleClick={() => {
                navigate('/j-lens');
                showToast('🧠 J-Lens Vault unlocked!', 'info');
              }}
              className="flex items-center gap-2 cursor-pointer select-none group text-left shrink-0 min-h-[44px] touch-target-safe"
              title="AzuraOS - Founder Operating System (Double-click for J-Lens)"
            >
              <div className="w-8 h-8 sm:w-8.5 sm:h-8.5 bg-[#151819] text-[#14B8A6] border border-[#1F2426] group-hover:border-[#14B8A6]/40 rounded-xl flex items-center justify-center font-display font-bold text-sm sm:text-base shadow-sm group-hover:scale-105 transition-all shrink-0">
                A
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <span className="font-display font-bold text-sm sm:text-base text-white tracking-tight leading-none whitespace-nowrap">
                  AzuraOS
                </span>
                <span className="text-[8px] sm:text-[9px] uppercase font-bold text-[#14B8A6] px-1.5 py-0.5 bg-[#14B8A6]/10 rounded-md border border-[#14B8A6]/30 shrink-0 whitespace-nowrap">
                  PRO
                </span>
              </div>
            </div>

            {!isOnline && (
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  const ts = getLastSyncTimestamp();
                  const timeStr = ts ? new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', month: 'short', day: 'numeric' }) : 'No cloud sync recorded yet';
                  showToast(`Offline Mode: Changes saved locally. Last cloud sync: ${timeStr}`, 'info');
                }}
                className="inline-flex items-center gap-1 text-[9px] font-bold text-amber-400 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 px-2 py-0.5 rounded-full animate-pulse shrink-0 cursor-pointer transition-all min-h-[36px]"
                title="Click for Cloud Sync details"
              >
                <CloudOff size={10} className="shrink-0 text-amber-400" />
                <span className="hidden xs:inline">Offline</span>
              </button>
            )}

            {/* Executive Mode Switcher: Chief of Staff Command vs CFO Suite */}
            <div className="hidden sm:inline-flex p-0.5 bg-[#121618] border border-[#232B2E] rounded-xl items-center text-[11px] font-bold">
              <button
                type="button"
                onClick={() => {
                  handleSetWorkspaceMode('quick');
                  navigate('/ai-assistant');
                }}
                className={`px-2.5 py-1 rounded-lg transition-all cursor-pointer flex items-center gap-1.5 ${
                  currentPath.includes('/ai-assistant') || currentPath === '/focus'
                    ? 'bg-[#14B8A6] text-black shadow-xs font-black' 
                    : 'text-gray-400 hover:text-white'
                }`}
                title="Chief of Staff Command Center (Strategy, Contracts & Pitch)"
              >
                <Sparkles size={11} className={currentPath.includes('/ai-assistant') || currentPath === '/focus' ? 'fill-black' : ''} />
                <span>Command Center</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  handleSetWorkspaceMode('power');
                  navigate('/dashboard');
                }}
                className={`px-2.5 py-1 rounded-lg transition-all cursor-pointer flex items-center gap-1.5 ${
                  !currentPath.includes('/ai-assistant') && currentPath !== '/focus' && currentPath !== '/'
                    ? 'bg-teal-500/20 text-teal-300 border border-teal-500/40 shadow-xs font-black' 
                    : 'text-gray-400 hover:text-white'
                }`}
                title="CFO Executive Suite (Taxes, Reconciler & Invoices)"
              >
                <Sliders size={11} />
                <span>CFO Suite</span>
              </button>
            </div>
          </div>

          {/* Right Header Controls: Clean Trust Badge & Hamburger Drop Down Trigger */}
          <div className="flex items-center gap-2 shrink-0">
            {isVoiceEnabled && (
              <button
                onClick={() => {
                  setVoiceModalInitialMode('voice_expense');
                  setIsVoiceModalOpen(true);
                }}
                className="h-9 sm:h-9 px-2.5 sm:px-3 flex items-center justify-center gap-1.5 rounded-xl text-xs font-semibold tracking-tight transition-all active:scale-95 cursor-pointer shrink-0 border bg-[#151819] hover:bg-[#1F2426] text-gray-200 hover:text-white border-[#2A2F31] hover:border-teal-500/40 shadow-xs group min-h-[44px] touch-target-safe"
                title="Dictate voice expenses, scopes & debriefs (Native Gemini Multimodal BYOK)"
              >
                <Mic size={14} className="text-teal-400 group-hover:scale-110 transition-transform" />
                <span className="hidden sm:inline text-[11px] font-mono font-medium text-teal-400/90">Voice Note</span>
              </button>
            )}

            <button
              onClick={() => setIsSecurityModalOpen(true)}
              className="h-9 sm:h-9 px-2.5 sm:px-3 flex items-center justify-center gap-1.5 rounded-xl text-xs font-semibold tracking-tight transition-all active:scale-95 cursor-pointer shrink-0 border bg-[#151819] hover:bg-[#1F2426] text-gray-200 hover:text-white border-[#2A2F31] hover:border-emerald-500/40 shadow-xs min-h-[44px] touch-target-safe"
              title="Trust, Certifications & Data Security Architecture (Google Cloud Partner)"
            >
              <ShieldCheck size={15} className="text-emerald-400" />
              <span className="hidden sm:inline text-[11px] font-mono font-medium text-emerald-400/90">Trust &amp; Security</span>
            </button>

            <button
              onClick={() => setIsNavDropdownOpen(!isNavDropdownOpen)}
              className={`h-9 sm:h-9 px-3 flex items-center justify-center gap-2 rounded-xl text-xs font-bold tracking-tight transition-all active:scale-95 cursor-pointer shrink-0 border group min-h-[44px] touch-target-safe ${
                isNavDropdownOpen
                  ? 'bg-[#14B8A6] text-black border-[#14B8A6] shadow-md shadow-[#14B8A6]/20'
                  : 'bg-[#151819] hover:bg-[#1F2426] text-white border-[#2A2F31] hover:border-[#14B8A6]/40 shadow-sm'
              }`}
              title="Open Navigation Menu"
              aria-label="Open Navigation Menu"
            >
              <Menu size={17} className={`transition-colors shrink-0 ${isNavDropdownOpen ? 'text-black' : 'text-[#14B8A6]'}`} />
              <span className="text-xs font-bold">{t('nav.menu')}</span>
            </button>
          </div>
        </div>
      </header>

      {/* 2. Full AI Studio Style Slide-Over Drawer Navigation */}
      {isNavDropdownOpen && (
        <>
          {/* Backdrop Blur Overlay */}
          <div 
            className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm transition-opacity duration-200"
            onClick={() => setIsNavDropdownOpen(false)}
            onTouchStart={() => setIsNavDropdownOpen(false)}
            onPointerDown={() => setIsNavDropdownOpen(false)}
          />

          {/* Slide-out Drawer Panel */}
          <aside className="fixed right-0 top-0 bottom-0 w-full sm:w-[380px] md:w-[410px] max-w-[100vw] bg-[#0E1011] border-l border-[#2A2F31] z-[101] flex flex-col shadow-2xl animate-in slide-in-from-right duration-200">
            
            {/* Drawer Header (Branding & Dismiss) */}
            <div className="p-4 sm:p-5 border-b border-[#1F2426] bg-[#121516]/80 flex items-start justify-between gap-3 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#151819] text-[#14B8A6] border border-[#1F2426] rounded-xl flex items-center justify-center font-display font-bold text-lg shadow-md shrink-0">
                  A
                </div>
                <div>
                  <div className="flex items-center gap-1.5">
                    <h2 className="font-display font-bold text-base text-white leading-tight">AzuraOS</h2>
                    <span className="text-[8px] uppercase font-bold text-[#14B8A6] px-1.5 py-0.5 bg-[#14B8A6]/10 rounded border border-[#14B8A6]/30">
                      PRO
                    </span>
                  </div>
                  <p className="text-[11px] text-gray-400 font-medium">Founder Operating System</p>
                </div>
              </div>
              <button
                onClick={() => setIsNavDropdownOpen(false)}
                className="p-1.5 text-gray-400 hover:text-white rounded-xl hover:bg-[#1F2426] transition-colors cursor-pointer"
                title="Close Menu"
              >
                <X size={18} />
              </button>
            </div>

            {/* Scrollable Navigation Body */}
            <div className="flex-1 overflow-y-auto custom-scrollbar p-3 sm:p-5 space-y-5">
              
              {/* Executive Mode Switcher (Drawer) */}
              <div className="p-3 bg-[#121516] border border-[#232B2E] rounded-2xl space-y-2">
                <div className="flex items-center justify-between text-xs font-bold text-gray-300">
                  <span>Executive Mode</span>
                  <span className="text-[10px] font-mono text-[#14B8A6]">
                    {currentPath.includes('/ai-assistant') || currentPath === '/focus' ? '👑 Chief of Staff' : '💰 CFO Suite'}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-1.5 p-1 bg-[#0A0D0E] rounded-xl border border-[#1A2224]">
                  <button
                    type="button"
                    onClick={() => {
                      handleSetWorkspaceMode('quick');
                      navigate('/ai-assistant');
                      setIsNavDropdownOpen(false);
                    }}
                    className={`py-1.5 px-2 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
                      currentPath.includes('/ai-assistant') || currentPath === '/focus'
                        ? 'bg-[#14B8A6] text-black shadow-xs font-black'
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    <Sparkles size={13} className={currentPath.includes('/ai-assistant') || currentPath === '/focus' ? 'fill-black' : ''} />
                    <span>Command Center</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      handleSetWorkspaceMode('power');
                      navigate('/dashboard');
                      setIsNavDropdownOpen(false);
                    }}
                    className={`py-1.5 px-2 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
                      !currentPath.includes('/ai-assistant') && currentPath !== '/focus' && currentPath !== '/'
                        ? 'bg-teal-500/20 text-teal-300 border border-teal-500/40 shadow-xs font-black'
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    <Sliders size={13} />
                    <span>CFO Suite</span>
                  </button>
                </div>
                <p className="text-[11px] text-gray-400 leading-tight">
                  {currentPath.includes('/ai-assistant') || currentPath === '/focus'
                    ? 'Active: Strategy, client agreements, pitch decks & non-sycophantic debriefs.'
                    : 'Active: Fractional CFO, margin defense, 0.05s bank statement reconciler & invoices.'}
                </p>
              </div>

              {/* BUCKET 1: CHIEF OF STAFF COMMAND CENTER */}
              <div className="space-y-1.5">
                <div className="text-[10px] font-bold uppercase tracking-wider text-teal-400 px-2 pb-1 flex items-center justify-between">
                  <span className="flex items-center gap-1.5">
                    <Sparkles size={11} />
                    <span>Chief of Staff Command</span>
                  </span>
                  <span className="text-[9px] text-teal-400 font-mono bg-teal-500/10 px-1.5 py-0.5 rounded border border-teal-500/20">CORE ENGINE</span>
                </div>
                <div className="grid grid-cols-1 gap-1">
                  {chiefOfStaffModules.map((mod) => {
                    const Icon = mod.icon;
                    const isActive = currentPath === mod.path;
                    return (
                      <button
                        key={mod.path}
                        onClick={() => {
                          setIsNavDropdownOpen(false);
                          if (mod.path === '#voice') {
                            setVoiceModalInitialMode('voice_expense');
                            setIsVoiceModalOpen(true);
                          } else {
                            navigate(mod.path);
                          }
                        }}
                        className={`w-full text-left px-3 py-2.5 rounded-xl text-xs transition-all flex items-center gap-3 cursor-pointer ${
                          isActive
                            ? 'bg-[#14B8A6]/15 text-[#14B8A6] font-bold border border-[#14B8A6]/30 shadow-xs'
                            : 'text-gray-300 hover:text-white hover:bg-[#151819]'
                        }`}
                      >
                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${
                          isActive 
                            ? 'bg-[#14B8A6] text-black' 
                            : 'bg-[#151819] text-gray-400 border border-[#1F2426]'
                        }`}>
                          <Icon size={14} />
                        </div>
                        <div className="flex flex-col min-w-0 flex-1">
                          <span className="font-semibold text-xs leading-tight truncate">{mod.label}</span>
                          <span className="text-[10px] text-gray-500 font-normal truncate">{mod.desc}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* BUCKET 2: CFO EXECUTIVE SUITE */}
              <div className="space-y-1.5 pt-2 border-t border-[#1F2426]">
                <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-400 px-2 pb-1 flex items-center justify-between">
                  <span className="flex items-center gap-1.5">
                    <DollarSign size={11} />
                    <span>CFO &amp; Treasury Suite</span>
                  </span>
                  <span className="text-[9px] text-emerald-400 font-mono bg-emerald-500/10 px-1.5 py-0.5 rounded border border-emerald-500/20">FINANCIAL ENGINE</span>
                </div>
                <div className="grid grid-cols-1 gap-1">
                  {cfoSuiteModules.map((mod) => {
                    const Icon = mod.icon;
                    const isActive = currentPath === mod.path;
                    return (
                      <button
                        key={mod.path}
                        onClick={() => {
                          setIsNavDropdownOpen(false);
                          navigate(mod.path);
                        }}
                        className={`w-full text-left px-3 py-2.5 rounded-xl text-xs transition-all flex items-center gap-3 cursor-pointer ${
                          isActive
                            ? 'bg-emerald-500/15 text-emerald-300 font-bold border border-emerald-500/30 shadow-xs'
                            : 'text-gray-300 hover:text-white hover:bg-[#151819]'
                        }`}
                      >
                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${
                          isActive 
                            ? 'bg-emerald-500 text-black' 
                            : 'bg-[#151819] text-gray-400 border border-[#1F2426]'
                        }`}>
                          <Icon size={14} />
                        </div>
                        <div className="flex flex-col min-w-0 flex-1">
                          <span className="font-semibold text-xs leading-tight truncate">{mod.label}</span>
                          <span className="text-[10px] text-gray-500 font-normal truncate">{mod.desc}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Founder Philosophy & Concept */}
              <div className="space-y-1.5 pt-2 border-t border-[#1F2426]">
                <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 px-2 pb-1">
                  {t('nav.manifesto')}
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  <button
                    onClick={() => {
                      setIsNavDropdownOpen(false);
                      navigate('/concept');
                    }}
                    className={`text-left px-3 py-2 rounded-xl text-xs transition-all flex items-center gap-2 cursor-pointer border ${
                      currentPath === '/concept'
                        ? 'bg-[#14B8A6]/10 text-[#14B8A6] border-[#14B8A6]/30 font-bold'
                        : 'bg-[#121516] text-gray-300 border-[#1F2426] hover:border-[#2A2F31] hover:text-white'
                    }`}
                  >
                    <Compass size={13} className="text-[#14B8A6] shrink-0" />
                    <span className="truncate">{t('nav.concept')}</span>
                  </button>
                  <button
                    onClick={() => {
                      setIsNavDropdownOpen(false);
                      setIsWhatIsAzuraOSModalOpen(true);
                    }}
                    className={`text-left px-3 py-2 rounded-xl text-xs transition-all flex items-center gap-2 cursor-pointer border ${
                      isWhatIsAzuraOSModalOpen || currentPath === '/what-is-azuraos'
                        ? 'bg-[#14B8A6]/10 text-[#14B8A6] border-[#14B8A6]/30 font-bold'
                        : 'bg-[#121516] text-gray-300 border-[#1F2426] hover:border-[#2A2F31] hover:text-white'
                    }`}
                  >
                    <HelpCircle size={13} className="text-[#14B8A6] shrink-0" />
                    <span className="truncate">{t('nav.what_is')}</span>
                  </button>
                </div>
              </div>

              {/* Quick Workspace Utilities */}
              <div className="space-y-1.5 pt-2 border-t border-[#1F2426]">
                <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 px-2 pb-1">
                  {t('nav.workspace_actions')}
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  <button
                    onClick={() => {
                      setIsNavDropdownOpen(false);
                      triggerQuickJSONBackup(
                        state.bookings || [],
                        state.expenses || [],
                        state.insights || [],
                        state.streams || []
                      );
                      showToast('📦 Workspace backup successfully downloaded!', 'success');
                    }}
                    className="text-left px-3 py-2 rounded-xl text-xs transition-all flex items-center gap-2 cursor-pointer bg-[#121516] text-gray-300 border border-[#1F2426] hover:border-[#2A2F31] hover:text-white"
                  >
                    <Download size={13} className="text-gray-400 shrink-0" />
                    <span className="truncate">{t('nav.export_records')}</span>
                  </button>
                  <button
                    onClick={() => {
                      setIsNavDropdownOpen(false);
                      const loaded = preloadDemo();
                      setState(loaded);
                      showToast('🚀 Sandbox Demo dataset reloaded!', 'success');
                      navigate('/dashboard');
                    }}
                    className="text-left px-3 py-2 rounded-xl text-xs transition-all flex items-center gap-2 cursor-pointer bg-[#121516] text-teal-400 border border-[#1F2426] hover:border-[#2A2F31] hover:text-teal-300"
                  >
                    <Sparkles size={13} className="text-teal-400 shrink-0" />
                    <span className="truncate">{t('nav.load_demo')}</span>
                  </button>
                </div>
              </div>

              {/* Partnerships & Backing Section */}
              <div className="space-y-1.5 pt-2 border-t border-[#1F2426]">
                <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 px-2 pb-1 flex items-center justify-between">
                  <span>Institutional Sublicenses</span>
                  <span className="text-[9px] text-teal-400 font-mono">Accelerators &amp; Hubs</span>
                </div>

                {/* Cohort Sublicense Portal Quick Trigger */}
                <button
                  onClick={() => {
                    setIsNavDropdownOpen(false);
                    navigate('/cohort-pilot');
                  }}
                  className="w-full text-left px-3 py-2.5 rounded-xl text-xs transition-all flex items-center justify-between gap-3 cursor-pointer bg-gradient-to-r from-teal-950/40 via-[#121516] to-emerald-950/20 border border-teal-500/40 hover:border-teal-400 text-gray-200 group shadow-xs"
                  title="Open Cohort Sponsorship & Institutional Voucher Gateway"
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="w-7 h-7 rounded-lg bg-teal-500/20 text-teal-300 border border-teal-500/30 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                      <Award size={14} />
                    </div>
                    <div className="flex flex-col min-w-0">
                      <span className="font-bold text-xs text-white truncate">🎓 Institutional Cohort Sublicense</span>
                      <span className="text-[10px] text-teal-300/90 font-mono truncate">Sponsored Workspace • Voucher Gateway</span>
                    </div>
                  </div>
                  <ArrowRight size={13} className="text-teal-400 shrink-0 group-hover:translate-x-0.5 transition-transform" />
                </button>

                <button
                  onClick={() => {
                    setIsNavDropdownOpen(false);
                    setIsContactModalOpen(true);
                  }}
                  className="w-full text-left px-3 py-2 rounded-xl text-xs transition-all flex items-center justify-between gap-3 cursor-pointer bg-[#121516] border border-[#2A2F31] hover:border-teal-400/50 hover:bg-[#151819] text-gray-300 group"
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="w-7 h-7 rounded-lg bg-[#1F2426] text-gray-300 border border-[#2A2F31] flex items-center justify-center shrink-0">
                      <Globe size={14} />
                    </div>
                    <div className="flex flex-col min-w-0">
                      <span className="font-semibold text-xs text-gray-200 truncate">{t('nav.request_partnership')}</span>
                      <span className="text-[10px] text-gray-400 truncate">{t('nav.partner_subtext')}</span>
                    </div>
                  </div>
                  <ArrowRight size={13} className="text-gray-400 shrink-0 group-hover:translate-x-0.5 transition-transform" />
                </button>
              </div>

              {/* Legal & Compliance Links */}
              <div className="space-y-2 pt-2 border-t border-[#1F2426]">
                <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 px-2 pb-0.5 flex items-center justify-between">
                  <span>{t('nav.legal_trust')}</span>
                  <span className="text-[9px] text-emerald-400 font-mono">SOC 1/2/3 &amp; ISO 27001 Cloud</span>
                </div>

                <button
                  onClick={() => {
                    setIsNavDropdownOpen(false);
                    setIsSecurityModalOpen(true);
                  }}
                  className="w-full text-left p-2.5 rounded-xl text-xs transition-all flex items-center justify-between gap-2 cursor-pointer bg-gradient-to-r from-emerald-500/15 via-[#151819] to-sky-500/10 border border-emerald-500/35 hover:border-emerald-400 text-gray-200 group shadow-xs"
                  title="View Google Cloud Partner Certification & AES-256 Vault"
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="w-7 h-7 rounded-lg bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 flex items-center justify-center shrink-0">
                      <ShieldCheck size={15} />
                    </div>
                    <div className="flex flex-col min-w-0">
                      <span className="font-bold text-xs text-white truncate">Trust, Certifications &amp; Security</span>
                      <span className="text-[10px] text-emerald-400/90 font-mono truncate">Google Cloud Partner • Zero-Trust</span>
                    </div>
                  </div>
                  <ArrowRight size={13} className="text-emerald-400 shrink-0 group-hover:translate-x-0.5 transition-transform" />
                </button>

                <div className="flex flex-wrap gap-2 text-[11px] text-gray-400 px-2 pt-1">
                  <button onClick={() => { setIsNavDropdownOpen(false); setIsSecurityModalOpen(true); }} className="hover:text-[#14B8A6] cursor-pointer">{t('nav.security')}</button>
                  <span className="text-gray-600">•</span>
                  <button onClick={() => { setIsNavDropdownOpen(false); setIsFAQModalOpen(true); }} className="hover:text-[#14B8A6] cursor-pointer">{t('nav.faqs')}</button>
                  <span className="text-gray-600">•</span>
                  <button onClick={() => { setIsNavDropdownOpen(false); setIsTermsModalOpen(true); }} className="hover:text-[#14B8A6] cursor-pointer">{t('nav.terms')}</button>
                  <span className="text-gray-600">•</span>
                  <button onClick={() => { setIsNavDropdownOpen(false); setIsPrivacyModalOpen(true); }} className="hover:text-[#14B8A6] cursor-pointer">{t('nav.privacy')}</button>
                  <span className="text-gray-600">•</span>
                  <button onClick={() => { setIsNavDropdownOpen(false); setIsRefundPolicyModalOpen(true); }} className="hover:text-[#14B8A6] cursor-pointer">{t('nav.refund')}</button>
                  <span className="text-gray-600">•</span>
                  <button onClick={() => { setIsNavDropdownOpen(false); setIsContactModalOpen(true); }} className="hover:text-[#14B8A6] cursor-pointer">{t('nav.contact')}</button>
                </div>
              </div>

            </div>

            {/* Dedicated Bottom Utility Block (Trial Days, Privacy Eye, Language, Bug Report, User Account & Sign Out) */}
            <div className="p-3 sm:p-4 border-t border-[#1F2426] bg-[#0A0C0D] space-y-2.5 shrink-0 shadow-2xl">
              
              {/* 1. Trial Countdown Days / Plan Status Banner */}
              {trialStatus.isOwner ? (
                <div className="flex items-center justify-between gap-2 px-3 py-1.5 rounded-xl bg-emerald-500/10 border border-emerald-500/25 text-emerald-400 text-[11px] font-mono font-bold">
                  <span className="flex items-center gap-1.5">
                    <Sparkles size={13} />
                    <span>{t('nav.master_access')}</span>
                  </span>
                  <span className="text-[9px] uppercase tracking-wider font-bold text-emerald-300">{t('nav.lifetime')}</span>
                </div>
              ) : isSubscribed ? (
                <div className="flex items-center justify-between gap-2 px-3 py-1.5 rounded-xl bg-teal-500/10 border border-teal-500/25 text-teal-300 text-[11px] font-mono font-bold">
                  <span className="flex items-center gap-1.5">
                    <ShieldCheck size={13} />
                    <span>{t('nav.pro_active')}</span>
                  </span>
                  <button
                    onClick={() => {
                      setIsNavDropdownOpen(false);
                      navigate('/billing');
                    }}
                    className="text-[10px] font-bold text-[#14B8A6] hover:underline cursor-pointer"
                  >
                    {t('nav.manage')}
                  </button>
                </div>
              ) : activeUser && trialStatus.isTrialActive ? (
                <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-xl bg-amber-500/15 border border-amber-500/35 text-amber-300 text-xs font-mono font-bold shadow-xs">
                  <span className="flex items-center gap-1.5 truncate">
                    <Clock size={13} className="text-amber-400 shrink-0" />
                    <span className="truncate">{t('nav.trial_days').replace('{days}', trialStatus.daysRemaining.toString())} ({trialStatus.hoursRemaining}h left)</span>
                  </span>
                  <button
                    onClick={() => {
                      setIsNavDropdownOpen(false);
                      navigate('/billing');
                    }}
                    className="text-[11px] font-bold text-[#14B8A6] hover:underline cursor-pointer shrink-0 ml-1"
                  >
                    {t('nav.upgrade')}
                  </button>
                </div>
              ) : activeUser ? (
                <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-xl bg-rose-500/15 border border-rose-500/35 text-rose-300 text-xs font-mono font-bold shadow-xs">
                  <span className="flex items-center gap-1.5 truncate">
                    <AlertTriangle size={13} className="text-rose-400 shrink-0" />
                    <span className="truncate">{t('nav.trial_expired')}</span>
                  </span>
                  <button
                    onClick={() => {
                      setIsNavDropdownOpen(false);
                      navigate('/billing');
                    }}
                    className="text-[11px] font-bold text-amber-400 hover:underline cursor-pointer shrink-0 ml-1"
                  >
                    {t('nav.unlock_pro')}
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-2 px-3 py-1.5 rounded-xl bg-amber-500/10 border border-amber-500/25 text-amber-300 text-[11px] font-mono font-bold">
                  <span className="flex items-center gap-1.5 truncate">
                    <Sparkles size={12} className="text-amber-400 shrink-0" />
                    <span className="truncate">7-Day Free Trial Available</span>
                  </span>
                  <button
                    onClick={() => {
                      setIsNavDropdownOpen(false);
                      setIsAuthModalOpen(true);
                    }}
                    className="text-[10px] font-bold text-[#14B8A6] hover:underline cursor-pointer shrink-0"
                  >
                    Start Free
                  </button>
                </div>
              )}

              {/* 2. Privacy Mode Eye Toggle (Visible Eye Off and On) */}
              <button
                onClick={togglePrivacyMode}
                className={`w-full py-2 px-3 rounded-xl text-xs font-semibold flex items-center justify-between gap-2 transition-all cursor-pointer border ${
                  isPrivacyMode
                    ? 'bg-amber-500/15 text-amber-400 border-amber-500/40 shadow-xs'
                    : 'bg-[#151819] text-gray-300 border-[#2A2F31] hover:text-white hover:border-[#3A3F41]'
                }`}
                title={isPrivacyMode ? "Click to reveal financial figures" : "Click to mask sensitive financial figures in public"}
              >
                <div className="flex items-center gap-2 min-w-0">
                  {isPrivacyMode ? <EyeOff size={14} className="text-amber-400 shrink-0" /> : <Eye size={14} className="text-gray-400 shrink-0" />}
                  <span className="font-bold text-xs truncate">
                    {isPrivacyMode ? t('nav.privacy_active') : t('nav.privacy_off')}
                  </span>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${
                  isPrivacyMode ? 'bg-amber-500/20 text-amber-300' : 'bg-[#1F2426] text-gray-400'
                }`}>
                  {isPrivacyMode ? t('nav.masked') : t('nav.visible')}
                </span>
              </button>

              {/* 3. Language Selector (Full Width, Zero Clipping, Opens Above Cleanly) */}
              <div className="w-full">
                <LanguageSelector compact dropUp fullWidth align="full" />
              </div>

              {/* 4. Action Utilities: Report Bug + Invite Founder (Spacious 2-column grid, generous padding, no text truncation) */}
              <div className="grid grid-cols-2 gap-2.5">
                <button
                  onClick={() => {
                    setIsNavDropdownOpen(false);
                    setIsFeedbackModalOpen(true);
                  }}
                  className="py-2.5 px-3 bg-[#151819] hover:bg-[#1F2426] text-gray-300 hover:text-amber-400 border border-[#2A2F31] rounded-xl text-xs font-semibold flex items-center justify-center gap-2 transition-all cursor-pointer shadow-xs group"
                  title="Report a bug or submit feedback"
                >
                  <Bug size={14} className="text-amber-400 shrink-0 group-hover:scale-110 transition-transform" />
                  <span className="font-semibold whitespace-nowrap">{t('nav.report_bug')}</span>
                </button>

                <button
                  onClick={() => {
                    setIsNavDropdownOpen(false);
                    setIsInviteModalOpen(true);
                  }}
                  className="py-2.5 px-3 bg-[#14B8A6]/10 hover:bg-[#14B8A6]/20 text-[#14B8A6] border border-[#14B8A6]/30 rounded-xl text-xs font-bold flex items-center justify-center gap-2 transition-all cursor-pointer shadow-xs group"
                  title="Invite a fellow founder to AzuraOS"
                >
                  <UserPlus size={14} className="shrink-0 group-hover:scale-110 transition-transform" />
                  <span className="whitespace-nowrap">{t('nav.invite_founder')}</span>
                </button>
              </div>

              {/* 4. Logged-in Email / Sign In / Log Out Section (At the very bottom of the menu) */}
              <div className="pt-2 border-t border-[#1F2426]">
                {activeUser ? (
                  <div className="flex items-center justify-between gap-2 bg-[#121516] p-2.5 rounded-xl border border-[#1F2426]">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="w-8 h-8 rounded-xl bg-[#14B8A6]/20 text-[#14B8A6] border border-[#14B8A6]/30 flex items-center justify-center font-bold text-xs uppercase shrink-0">
                        {(activeUser.displayName || activeUser.email || 'F').charAt(0)}
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-bold text-white truncate max-w-[170px] sm:max-w-[200px]" title={activeUser.email || ''}>
                          {activeUser.email}
                        </p>
                        <div className="flex items-center gap-1.5 text-[10px] text-emerald-400 font-medium">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse shrink-0" />
                          <span className="truncate">Private Vault Connected</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => {
                          setIsNavDropdownOpen(false);
                          navigate('/settings');
                        }}
                        className="p-1.5 text-gray-400 hover:text-white hover:bg-[#1F2426] rounded-lg transition-colors cursor-pointer"
                        title="Business Settings"
                      >
                        <Settings size={15} />
                      </button>
                      <button
                        onClick={handleLogout}
                        className="p-1.5 text-gray-400 hover:text-rose-400 hover:bg-rose-500/10 rounded-lg transition-colors cursor-pointer"
                        title="Sign Out"
                      >
                        <LogOut size={15} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      setIsNavDropdownOpen(false);
                      setIsAuthModalOpen(true);
                    }}
                    className="w-full py-2.5 px-3 bg-[#14B8A6] hover:bg-[#0D9488] text-black font-bold rounded-xl text-xs flex items-center justify-center gap-2 transition-all cursor-pointer shadow-md shadow-[#14B8A6]/20"
                  >
                    <LogIn size={15} />
                    <span>{t('nav.founder_access')}</span>
                  </button>
                )}
              </div>

            </div>

          </aside>
        </>
      )}

      {/* Live Profitability Engine Bar (Rendered inside active application workspace) */}
      {!isLandingOrConcept && currentPath !== '/book' && (
        <div className="bg-[#0C0E0F] border-b border-[#1F2426] px-4 sm:px-8 py-3.5 shadow-sm">
          <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-3 sm:gap-4">
              <div className={`w-2.5 h-2.5 rounded-full ${statusColors.textColor} bg-current animate-pulse shrink-0`} style={{ boxShadow: `0 0 10px ${statusColors.glowColor}` }} />
              <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-3">
                <span className="text-[10px] text-gray-500 uppercase tracking-wider font-bold">{t('nav.live_margin')}</span>
                <div className="flex items-baseline gap-1.5">
                  <span className={`text-2xl font-black font-mono tracking-tight ${statusColors.textColor}`}>
                    {marginVal.toFixed(1)}%
                  </span>
                  <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold border ${statusColors.badgeClass}`}>
                    {statusColors.label}
                  </span>
                </div>
              </div>

              {/* Real-time Figures Pill (Telemetry Display with Privacy Blurring) */}
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 bg-[#151819] border border-[#2A2F31] rounded-xl px-2.5 sm:px-3 py-1.5 text-xs font-mono">
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] text-gray-500 uppercase font-bold tracking-wider">Net:</span>
                  <PrivacyAmount 
                    amount={netProfit} 
                    isPrivacyMode={isPrivacyMode} 
                    className={`font-bold text-xs ${netProfit >= 0 ? 'text-[#14B8A6]' : 'text-rose-400'}`}
                  />
                </div>
                <div className="flex items-center gap-1.5 sm:gap-2 border-l border-[#2A2F31] pl-2 sm:pl-3 text-[11px]">
                  <span className="text-gray-400 flex items-center gap-1" title="Total Invoiced Revenue">
                    <span className="text-gray-500 text-[10px] uppercase font-bold">Rev:</span>
                    <PrivacyAmount amount={totalRevenue} isPrivacyMode={isPrivacyMode} className="text-gray-200" />
                  </span>
                  <span className="text-gray-600">•</span>
                  <span className="text-gray-400 flex items-center gap-1" title="Total Recorded Expenses">
                    <span className="text-gray-500 text-[10px] uppercase font-bold">Exp:</span>
                    <PrivacyAmount amount={totalExpenses} isPrivacyMode={isPrivacyMode} className="text-gray-200" />
                  </span>
                </div>
              </div>
            </div>

            {/* Interactive User-defined Threshold Control */}
            <div className="flex items-center gap-3 bg-[#151819] border border-[#2A2F31] rounded-xl px-3 py-1.5 shrink-0">
              <Sliders size={12} className="text-gray-400" />
              <span className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider">{t('nav.target_safe_zone')}</span>
              <div className="flex items-center gap-1.5">
                <button 
                  onClick={() => {
                    const newVal = Math.max(0, marginThreshold - 1);
                    setMarginThreshold(newVal);
                    localStorage.setItem('solostack_margin_threshold', newVal.toString());
                  }}
                  className="w-7 h-7 sm:w-5 sm:h-5 bg-[#0C0E0F] border border-[#2A2F31] rounded-lg sm:rounded flex items-center justify-center text-xs text-gray-400 hover:text-white hover:border-gray-500 active:bg-[#151819] transition-colors cursor-pointer select-none"
                  title="Decrease safety threshold"
                >
                  -
                </button>
                <input
                  type="number"
                  value={marginThreshold}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value);
                    if (!isNaN(val)) {
                      setMarginThreshold(val);
                      localStorage.setItem('solostack_margin_threshold', val.toString());
                    }
                  }}
                  className="w-8 bg-transparent text-center text-xs font-mono font-bold text-white focus:outline-none"
                />
                <span className="text-[10px] text-gray-500 font-mono">%</span>
                <button 
                  onClick={() => {
                    const newVal = Math.min(100, marginThreshold + 1);
                    setMarginThreshold(newVal);
                    localStorage.setItem('solostack_margin_threshold', newVal.toString());
                  }}
                  className="w-7 h-7 sm:w-5 sm:h-5 bg-[#0C0E0F] border border-[#2A2F31] rounded-lg sm:rounded flex items-center justify-center text-xs text-gray-400 hover:text-white hover:border-gray-500 active:bg-[#151819] transition-colors cursor-pointer select-none"
                  title="Increase safety threshold"
                >
                  +
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 2. Main Area content with GPU state isolation */}
      <main className="flex-1 max-w-5xl w-full mx-auto px-4 sm:px-8 py-6 sm:py-8 relative state-view-wrapper">
        
        {/* Dynamic Toast Notifications */}
        {notification && (
          <div className="fixed top-20 right-4 sm:right-8 z-50 bg-[#151819] text-white border border-[#2A2F31] px-4 py-3.5 rounded-2xl shadow-xl flex items-center gap-3 animate-slide-in text-xs font-medium max-w-sm">
            <span className="p-1 bg-[#14B8A6]/20 text-[#14B8A6] rounded-lg text-sm font-bold">✓</span>
            <div>{notification.message}</div>
          </div>
        )}

        {/* Real-Time Feedback Loop Alert Box (Immediately showing margin impacts) */}
        {lastMarginChange && (
          <div className={`mb-6 border rounded-2xl p-4.5 shadow-lg relative overflow-hidden transition-all duration-300 animate-slide-in ${
            lastMarginChange.newMargin < lastMarginChange.threshold 
              ? 'bg-rose-950/10 border-rose-500/30' 
              : 'bg-[#151819] border-[#2A2F31]'
          }`}>
            <button 
              onClick={() => setLastMarginChange(null)}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-300 transition-colors p-1"
              title="Dismiss Alert"
            >
              <X size={16} />
            </button>
            
            <div className="flex items-start gap-3.5 pr-6">
              <div className={`p-2.5 rounded-xl shrink-0 ${
                lastMarginChange.newMargin < lastMarginChange.threshold
                  ? 'bg-rose-500/15 text-rose-400'
                  : 'bg-[#14B8A6]/15 text-[#14B8A6]'
              }`}>
                {lastMarginChange.newMargin < lastMarginChange.threshold ? (
                  <AlertTriangle size={18} />
                ) : (
                  <Sparkles size={18} />
                )}
              </div>
              
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase font-bold tracking-wider text-gray-500">
                    REAL-TIME MARGIN FEEDBACK
                  </span>
                  <span className="text-[9px] text-gray-600">• Just Now</span>
                </div>
                
                <h4 className="text-sm font-semibold text-white leading-snug">
                  {lastMarginChange.type === 'booking' ? 'Income' : lastMarginChange.type === 'expense' ? 'Expense' : 'Record Change'} Impact: {' '}
                  <span className="text-gray-400 font-normal">"{lastMarginChange.itemName}"</span>
                </h4>
                
                <p className="text-xs text-gray-300 leading-relaxed pt-1 flex flex-wrap items-center gap-1.5">
                  Margin moved from{' '}
                  <span className="font-mono font-bold text-gray-400">{lastMarginChange.oldMargin.toFixed(1)}%</span>
                  <span className="text-gray-500">→</span>
                  <span className={`font-mono font-bold ${
                    lastMarginChange.newMargin < lastMarginChange.threshold ? 'text-rose-400' : 'text-[#14B8A6]'
                  }`}>
                    {lastMarginChange.newMargin.toFixed(1)}%
                  </span>
                  
                  {lastMarginChange.newMargin < lastMarginChange.oldMargin ? (
                    <span className="text-rose-400 text-[10px] flex items-center font-bold">
                      (<TrendingDown size={11} className="mr-0.5 inline" /> -{(lastMarginChange.oldMargin - lastMarginChange.newMargin).toFixed(1)}%)
                    </span>
                  ) : (
                    <span className="text-[#14B8A6] text-[10px] flex items-center font-bold">
                      (<TrendingUp size={11} className="mr-0.5 inline" /> +{(lastMarginChange.newMargin - lastMarginChange.oldMargin).toFixed(1)}%)
                    </span>
                  )}
                </p>

                {lastMarginChange.crossedBelow && (
                  <div className="mt-2.5 flex items-center gap-2 text-rose-400 font-bold text-xs bg-rose-500/10 rounded-lg py-1.5 px-3 border border-rose-500/20">
                    <AlertTriangle size={14} className="animate-bounce" />
                    <span>⚠️ You’ve crossed below your safe margin threshold ({lastMarginChange.threshold}%)!</span>
                  </div>
                )}
                
                {lastMarginChange.crossedAbove && (
                  <div className="mt-2.5 flex items-center gap-2 text-[#14B8A6] font-bold text-xs bg-[#14B8A6]/10 rounded-lg py-1.5 px-3 border border-[#14B8A6]/20">
                    <CheckCircle size={14} />
                    <span>🎉 Outstanding! You've successfully recovered above your safe margin threshold ({lastMarginChange.threshold}%)!</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Dynamic View Injection */}
        <Suspense fallback={
          <div className="py-24 flex flex-col items-center justify-center space-y-3">
            <div className="w-8 h-8 rounded-full border-2 border-teal-500 border-t-transparent animate-spin" />
            <span className="text-xs font-mono text-gray-500">Loading workspace...</span>
          </div>
        }>
          {renderActiveView()}
        </Suspense>
      </main>

      {/* 3. Streamlined Global Footer */}
      <footer className="border-t border-[#1A1F21] pt-8 pb-36 md:pb-12 px-4 sm:px-8 bg-[#090B0C] text-center text-xs text-gray-500">
        <div className="max-w-4xl mx-auto flex flex-col items-center gap-5">
          {/* Navigation & Policies Cluster */}
          <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 text-xs w-full">
            <button
              type="button"
              onClick={() => setIsWhatIsAzuraOSModalOpen(true)}
              className="px-3 py-1 rounded-full bg-[#14B8A6]/10 hover:bg-[#14B8A6]/20 border border-[#14B8A6]/30 text-[#14B8A6] font-semibold transition-all cursor-pointer flex items-center gap-1.5"
            >
              <Sparkles size={12} />
              <span>What is AzuraOS?</span>
            </button>

            <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs text-gray-400 font-medium">
              <button
                type="button"
                onClick={() => setIsTermsModalOpen(true)}
                className="hover:text-[#14B8A6] transition-colors cursor-pointer"
              >
                Terms
              </button>
              <span className="text-gray-700 select-none">•</span>
              <button
                type="button"
                onClick={() => setIsPrivacyModalOpen(true)}
                className="hover:text-[#14B8A6] transition-colors cursor-pointer"
              >
                Privacy
              </button>
              <span className="text-gray-700 select-none">•</span>
              <button
                type="button"
                onClick={() => setIsSecurityModalOpen(true)}
                className="hover:text-[#14B8A6] transition-colors cursor-pointer"
              >
                Security
              </button>
              <span className="text-gray-700 select-none">•</span>
              <button
                type="button"
                onClick={() => setIsRefundPolicyModalOpen(true)}
                className="hover:text-[#14B8A6] transition-colors cursor-pointer"
              >
                Refunds
              </button>
              <span className="text-gray-700 select-none">•</span>
              <button
                type="button"
                onClick={() => setIsFAQModalOpen(true)}
                className="hover:text-[#14B8A6] transition-colors cursor-pointer"
              >
                FAQs
              </button>
              <span className="text-gray-700 select-none">•</span>
              <button
                type="button"
                onClick={() => setIsSecurityModalOpen(true)}
                className="hover:text-[#14B8A6] transition-colors cursor-pointer"
              >
                Architecture
              </button>
              <span className="text-gray-700 select-none">•</span>
              <button
                type="button"
                onClick={() => setIsContactModalOpen(true)}
                className="hover:text-[#14B8A6] transition-colors cursor-pointer"
              >
                Support
              </button>
              <span className="text-gray-700 select-none">•</span>
              <button
                type="button"
                onClick={() => setIsTermsModalOpen(true)}
                className="hover:text-[#14B8A6] transition-colors cursor-pointer"
              >
                DMCA
              </button>
            </div>
          </div>

          {/* Corporate Identity & Sovereign Attribution */}
          <div className="space-y-1.5 text-center">
            <p className="text-gray-300 font-semibold text-xs sm:text-sm">
              © 2026 AzuraOS — Founder Operating System
            </p>
            <p className="text-[11px] text-teal-400/90 font-mono">
              A sovereign product of <strong>Azura Seraph™ (Pty) Ltd</strong> <span className="text-gray-500 font-sans">(Reg: 2024/560516/07) • South Africa</span>
            </p>
            <p className="text-[11px] text-gray-500 font-medium">
              Clarity, control, and sovereign ownership. No surveillance. No data games.
            </p>
          </div>

          {/* Partner & Ecosystem Micro-Badges */}
          <div className="flex flex-wrap items-center justify-center gap-2 text-[10px] sm:text-[11px]">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#121617] border border-[#202729] text-gray-300">
              <span className="w-1.5 h-1.5 rounded-full bg-[#76B900]"></span>
              NVIDIA Inception Member
            </span>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#121617] border border-[#202729] text-gray-400">
              <span className="w-1.5 h-1.5 rounded-full bg-teal-400"></span>
              Google Cloud Partner Network
            </span>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#121617] border border-[#202729] text-gray-400">
              <span className="w-1.5 h-1.5 rounded-full bg-teal-400"></span>
              Google for Startups Cloud
            </span>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#121617] border border-[#202729] text-gray-400">
              <span className="w-1.5 h-1.5 rounded-full bg-teal-400"></span>
              Women in Tech South Africa
            </span>
          </div>

          {/* Expandable Legal & AI Advisory Disclosure */}
          <div className="flex flex-col items-center w-full max-w-lg">
            <button
              type="button"
              onClick={() => setIsAdvisoryOpen(!isAdvisoryOpen)}
              className="inline-flex items-center gap-1.5 text-[11px] text-gray-400 hover:text-gray-200 transition-colors cursor-pointer py-0.5"
            >
              <ShieldCheck size={13} className="text-[#14B8A6]" />
              <span>Legal &amp; AI Sovereign BYOK Advisory</span>
              <ChevronDown size={12} className={`transition-transform duration-200 ${isAdvisoryOpen ? 'rotate-180' : ''}`} />
            </button>
            {isAdvisoryOpen && (
              <div className="mt-2 text-[11px] text-gray-400 leading-relaxed bg-[#121617] p-3 rounded-xl border border-[#22292C] text-left animate-fade-in shadow-xl w-full">
                <strong className="text-gray-300 font-semibold inline mr-1">Compliance Notice:</strong>
                AzuraOS provides self-managed business workflow software (not a bank, registered auditor, or licensed tax advisor). AI features operate on a sovereign BYOK model; you manage your own Google AI key and associated usage costs.
              </div>
            )}
          </div>

          {/* Clean Sandbox Controls Pill */}
          <div className="pt-1 flex items-center justify-center">
            <div className="inline-flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 text-xs bg-[#121516] px-3.5 py-1.5 rounded-full border border-[#202628] shadow-sm">
              <button 
                onClick={() => {
                  const loaded = preloadDemo();
                  setState(loaded);
                  showToast('🚀 Sandbox Demo dataset loaded! Explore realistic bookings, expenses, and insights.', 'success');
                  navigate('/dashboard');
                }}
                className="text-teal-400 hover:text-teal-300 font-bold transition-colors flex items-center gap-1 cursor-pointer text-[11px]"
                title="Load full pre-populated demo dataset into sandbox"
              >
                <Sparkles size={12} className="text-teal-400" /> 
                <span>Load Demo Data</span>
              </button>
              <span className="text-gray-700 select-none">•</span>
              <span className="flex items-center gap-1.5 font-mono text-gray-300 text-[11px]">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> Sandbox Active
              </span>
              <span className="text-gray-700 select-none">•</span>
              {confirmingReset ? (
                <span className="flex items-center gap-1.5 bg-rose-500/10 border border-rose-500/20 rounded-lg px-2 py-0.5 text-[11px] font-medium text-rose-300 animate-fade-in shrink-0">
                  <span>Clear data?</span>
                  <button
                    onClick={() => {
                      const cleared = clearAllData();
                      setState(cleared);
                      setConfirmingReset(false);
                      showToast('Sandbox reset to zero. Setup custom streams!', 'info');
                      navigate('/dashboard');
                    }}
                    className="text-white hover:text-rose-200 bg-rose-500/20 px-1.5 py-0.5 rounded font-bold cursor-pointer text-[10px] transition-colors"
                  >
                    Yes, clear
                  </button>
                  <button
                    onClick={() => setConfirmingReset(false)}
                    className="text-gray-400 hover:text-white cursor-pointer text-[10px]"
                  >
                    Cancel
                  </button>
                </span>
              ) : (
                <button 
                  onClick={() => setConfirmingReset(true)}
                  className="text-gray-400 hover:text-rose-400 font-medium transition-colors cursor-pointer text-[11px]"
                >
                  Clear Sandbox
                </button>
              )}
            </div>
          </div>
        </div>
      </footer>

      {/* 4. FLOATING GLOBAL "+ INSIGHT" BUTTON (Only visible once onboarding is complete, and not on landing pages!) */}
      {!( !state.streams || state.streams.length === 0 ) && currentPath !== '/about' && (
        <button
          onClick={() => triggerAddInsight('general', '')}
          title="Capture J-Lens Note / Insight"
          className="fixed bottom-20 sm:bottom-22 right-4 sm:right-6 w-14 h-14 bg-[#14B8A6] hover:bg-[#0EA5A4] text-black rounded-full flex items-center justify-center shadow-2xl hover:scale-105 active:scale-95 transition-all z-40 group border border-[#14B8A6]/30 scale-100 opacity-100"
        >
          <Plus size={24} className="text-black stroke-[3px] group-hover:rotate-90 transition-transform duration-300" />
        </button>
      )}

      {/* 5. Modals Injection */}
      <AddBookingModal
        isOpen={isBookingOpen}
        onClose={() => setIsBookingOpen(false)}
        onSubmit={handleCreateBooking}
        streams={state.streams || []}
      />

      <AddExpenseModal
        isOpen={isExpenseOpen}
        onClose={() => setIsExpenseOpen(false)}
        onSubmit={handleCreateExpense}
        streams={state.streams || []}
      />


      <AddInsightModal
        isOpen={isInsightOpen}
        onClose={() => setIsInsightOpen(false)}
        onSubmit={handleCreateInsight}
        bookings={state.bookings}
        expenses={state.expenses}
        prefilledType={insightPrefilledType}
        prefilledId={insightPrefilledId}
      />

      {isImportOpen && (
        <Suspense fallback={null}>
          <CSVImportModal
            isOpen={isImportOpen}
            onClose={() => setIsImportOpen(false)}
            type={importType}
            streams={state.streams || []}
            onImportComplete={handleImportComplete}
          />
        </Suspense>
      )}

      {isReconcileModalOpen && (
        <Suspense fallback={null}>
          <AIReconciliationHub
            isOpen={isReconcileModalOpen}
            onClose={() => setIsReconcileModalOpen(false)}
            streams={state.streams || []}
            onReconcileSuccess={(summary) => {
              setState(getDbState());
              showToast(`✨ Reconciled ${summary.bookingsAdded} bookings & ${summary.expensesAdded} expenses!`, 'success');
            }}
            showToast={showToast}
          />
        </Suspense>
      )}

      {isAuthModalOpen && (
        <Suspense fallback={null}>
          <AuthModal
            isOpen={isAuthModalOpen}
            initialEmail={authModalInitialEmail}
            onClose={() => {
              setIsAuthModalOpen(false);
              setAuthModalInitialEmail('');
            }}
            onSuccess={(userEmail) => {
              setLocalUserEmail(userEmail);
              localStorage.setItem('solostack_local_user_email', userEmail);
              showToast(`Cloud Vault active for ${userEmail}`);
              setAuthModalInitialEmail('');
              navigate('/welcome');
            }}
            showToast={showToast}
            onOpenTerms={() => setIsTermsModalOpen(true)}
            onOpenPrivacy={() => setIsPrivacyModalOpen(true)}
          />
        </Suspense>
      )}

      {isInviteModalOpen && (
        <Suspense fallback={null}>
          <InviteFounderModal
            isOpen={isInviteModalOpen}
            onClose={() => setIsInviteModalOpen(false)}
          />
        </Suspense>
      )}

      {isTermsModalOpen && (
        <Suspense fallback={null}>
          <TermsModal
            isOpen={isTermsModalOpen}
            onClose={() => setIsTermsModalOpen(false)}
            onOpenLogin={() => {
              setIsAuthModalOpen(true);
            }}
          />
        </Suspense>
      )}

      {isPrivacyModalOpen && (
        <Suspense fallback={null}>
          <PrivacyModal
            isOpen={isPrivacyModalOpen}
            onClose={() => setIsPrivacyModalOpen(false)}
            onOpenLogin={() => {
              setIsAuthModalOpen(true);
            }}
          />
        </Suspense>
      )}

      {isSecurityModalOpen && (
        <Suspense fallback={null}>
          <SecurityComplianceModal
            isOpen={isSecurityModalOpen}
            onClose={() => setIsSecurityModalOpen(false)}
            showToast={showToast}
          />
        </Suspense>
      )}

      {isFAQModalOpen && (
        <Suspense fallback={null}>
          <FAQModal
            isOpen={isFAQModalOpen}
            onClose={() => setIsFAQModalOpen(false)}
            onOpenSecurity={() => setIsSecurityModalOpen(true)}
            onOpenAuthModal={() => setIsAuthModalOpen(true)}
          />
        </Suspense>
      )}

      {isContactModalOpen && (
        <Suspense fallback={null}>
          <ContactModal
            isOpen={isContactModalOpen}
            onClose={() => setIsContactModalOpen(false)}
            onOpenFAQ={() => setIsFAQModalOpen(true)}
          />
        </Suspense>
      )}

      {isRefundPolicyModalOpen && (
        <Suspense fallback={null}>
          <RefundPolicyModal
            isOpen={isRefundPolicyModalOpen}
            onClose={() => setIsRefundPolicyModalOpen(false)}
            onOpenContact={() => setIsContactModalOpen(true)}
            onOpenTerms={() => setIsTermsModalOpen(true)}
          />
        </Suspense>
      )}

      {isFeedbackModalOpen && (
        <Suspense fallback={null}>
          <FeedbackBugModal
            isOpen={isFeedbackModalOpen}
            onClose={() => setIsFeedbackModalOpen(false)}
            showToast={showToast}
            currentUser={currentUser}
            currentUserEmail={currentUser?.email || activeUser?.email || undefined}
            currentView={currentPath}
          />
        </Suspense>
      )}

      {isWhatIsAzuraOSModalOpen && (
        <Suspense fallback={null}>
          <WhatIsAzuraOSModal
            isOpen={isWhatIsAzuraOSModalOpen}
            onClose={() => setIsWhatIsAzuraOSModalOpen(false)}
            onStartTrial={() => {
              setIsWhatIsAzuraOSModalOpen(false);
              navigate('/welcome');
            }}
          />
        </Suspense>
      )}

      {/* ⚡ 10-Second Quick Start Wizard */}
      <QuickStartWizard
        isOpen={isQuickStartOpen}
        onClose={() => setIsQuickStartOpen(false)}
        onComplete={(mode) => {
          setIsQuickStartOpen(false);
          handleSetWorkspaceMode(mode);
          navigate('/dashboard');
        }}
        showToast={showToast}
      />

      {/* 🎙️ NATIVE GEMINI MULTIMODAL AUDIO TRANSCRIBER & ACTION ENGINE (BYOK) */}
      {isVoiceEnabled && isVoiceModalOpen && (
        <Suspense fallback={null}>
          <VoiceTranscriptionModal
            isOpen={isVoiceModalOpen}
            onClose={() => setIsVoiceModalOpen(false)}
            streams={state.streams || []}
            bookings={state.bookings || []}
            expenses={state.expenses || []}
            currentUser={currentUser}
            onRequireAuth={() => {
              setIsVoiceModalOpen(false);
              setIsAuthModalOpen(true);
            }}
            showToast={showToast}
            onDataUpdated={(newState) => setState(newState)}
            initialMode={voiceModalInitialMode}
          />
        </Suspense>
      )}

      {/* Floating Scroll Navigation (Scroll to Bottom / Top) */}
      <ScrollNavigator isHidden={isConciergeOpen || isInteractiveTourActive} />

      {/* Financial Data Safety Backup Reminder Toast/Prompt */}
      <BackupReminderToast
        bookings={state.bookings}
        expenses={state.expenses}
        insights={state.insights || []}
        streams={state.streams || []}
        onOpenExportCenter={() => navigate('/hq')}
        showToast={showToast}
      />

      {/* Progressive Web App (PWA) Update Banner & Install Prompt */}
      <PWAUpdateBanner />
      <PWAInstallPrompt />

      {/* Official In-App AI Concierge & Copilot Assistant */}
      {!isInteractiveTourActive && (
        <AIConciergeWidget
          onNavigate={navigate}
          showToast={showToast}
          isOpen={isConciergeOpen}
          onToggleOpen={() => setIsConciergeOpen(!isConciergeOpen)}
          onOpenWhatIsAzuraOS={() => setIsWhatIsAzuraOSModalOpen(true)}
          onOpenContact={() => setIsContactModalOpen(true)}
          onOpenFAQ={() => setIsFAQModalOpen(true)}
          onOpenSecurity={() => setIsSecurityModalOpen(true)}
        />
      )}

      {/* Progressive In-App Lifecycle Prompts for Day 1, 2, 3 */}
      <TrialLifecyclePrompts
        trialStatus={trialStatus}
        isSubscribed={isSubscribed}
        userEmail={activeUser?.email}
        onNavigate={navigate}
      />

      {/* Global Hard Paywall Lockout Modal (Triggered on Day 4+ when trial expires) */}
      {isHardPaywallActive && (
        <Suspense fallback={null}>
          <HardPaywallLockoutModal
            userEmail={activeUser?.email}
            showToast={showToast}
            onSubscriptionSuccess={() => {
              showToast('🎉 Sovereign Founder Pro subscription activated!', 'success');
            }}
            onNavigate={navigate}
            onLogout={handleLogout}
            onOpenConcept={() => navigate('/what-is-azuraos')}
            onOpenSecurity={() => setIsSecurityModalOpen(true)}
            onExportData={() => {
              triggerQuickJSONBackup(
                state.bookings || [],
                state.expenses || [],
                state.insights || [],
                state.streams || []
              );
              showToast('📦 Workspace backup successfully downloaded!', 'success');
            }}
          />
        </Suspense>
      )}

      {/* Global Cohort Onboarding & Sublicense Voucher Modal */}
      {isCohortModalOpen && (
        <Suspense fallback={null}>
          <CohortOnboardingModal
            isOpen={isCohortModalOpen}
            onClose={() => setIsCohortModalOpen(false)}
            onActivated={(newSettings) => {
              setIsCohortModalOpen(false);
              showToast(`🎓 ${newSettings.cohortName} sponsorship activated!`, 'success');
            }}
            showToast={showToast}
          />
        </Suspense>
      )}

    </div>
  );
}
